import React from 'react';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx, tokens } from '../../theme/tokens';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUnitOverride, updateUnitContent } from '../../services/unitOverridesService';

const PAGE_ID = 'm10_u2_x2_CAD';

type SignalType = 'BOOL';
type DeviceVoltage = 24 | 230;
type RelayMode = 'ON_DELAY' | 'OFF_DELAY' | 'PULSE' | 'AV' | 'RV' | 'TI' | 'TP' | 'IA' | 'EW' | 'AW' | 'ARV' | 'ON' | 'OFF';

type CadNodeType =
  | 'L_SOURCE'
  | 'L2_SOURCE'
  | 'L3_SOURCE'
  | 'N_SOURCE'
  | 'PE_SOURCE'
  | 'SWITCH_NO'
  | 'CROSS_SWITCH'
  | 'TASTER_NO'
  | 'SENSOR_NO'
  | 'SWITCH_PILOT'
  | 'FUSE'
  | 'RELAY_COIL'
  | 'RELAY_4CO'
  | 'CONTACTOR_COIL'
  | 'MOTOR_CONTACTOR'
  | 'MOTOR_AC'
  | 'MOTOR_3PH'
  | 'EMERGENCY_STOP_NC'
  | 'OVERLOAD_RELAY'
  | 'DIMMER'
  | 'BUZZER'
  | 'BLINKER'
  | 'TIMER_RELAY'
  | 'MULTIFUNCTION_RELAY'
  | 'CHANGEOVER'
  | 'TRANSFORMER'
  | 'SOCKET'
  | 'LAMP'
  | 'JUNCTION'
  | 'JUNCTION_BOX';

type Pin = {
  id: string;
  nodeId: string;
  isInput: boolean;
  name: string;
  signalType: SignalType;
};

type CadNode = {
  id: string;
  type: CadNodeType;
  label: string;
  displayName?: string;
  x: number;
  y: number;
  inputs: Pin[];
  outputs: Pin[];
  state: {
    active?: boolean;
    ratedVoltage?: DeviceVoltage;
    burned?: boolean;
    timerMs?: number;
    relayMode?: RelayMode;
    dimmerPercent?: number;
  };
};

type Connection = {
  id: string;
  fromPinId: string;
  toPinId: string;
  routePoints?: { x: number; y: number }[];
};

type RouteMode = 'DIRECT' | 'FREE';

type MotorDirection = 'CW' | 'CCW';

type MotorVisualState = {
  running: boolean;
  direction: MotorDirection;
};

type SnackbarState = {
  open: boolean;
  severity: 'success' | 'error' | 'warning' | 'info';
  message: string;
};

type TaskVector = {
  inputs: Record<string, boolean>;
  expected: Record<string, boolean>;
};

type CadTask = {
  title: string;
  description: string;
  requiredTypes: CadNodeType[];
  requiredLabels?: string[];
  vectors: TaskVector[];
};

type ValidationResult = {
  vectorIndex: number;
  inputs: Record<string, boolean>;
  expected: Record<string, boolean>;
  actual: Record<string, boolean>;
  pass: boolean;
};

type NodeDefinition = {
  label: string;
  inputs: string[];
  outputs: string[];
  toggleable?: boolean;
  supportsVoltageSelection?: boolean;
  defaultVoltage?: DeviceVoltage;
  description: string;
};

type PotentialKey = 'L1' | 'L2' | 'L3' | 'N' | 'PE' | string | null;

type CircuitSimulationResult = {
  outputValues: Record<string, boolean>;
  lampStates: Record<string, boolean>;
  motorStates: Record<string, MotorVisualState>;
  newlyBurnedNodeIds: string[];
  shortCircuitMessages: string[];
};

type DragState = {
  nodeId: string;
  offsetX: number;
  offsetY: number;
};

const createId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Math.random().toString(16).slice(2)}-${Date.now()}`;
};

const GRID_SIZE = 24;
const GRID_X_STEP = GRID_SIZE * 13;
const GRID_Y_STEP = GRID_SIZE * 9;
const GRID_START_X = GRID_SIZE * 3;
const GRID_START_Y = GRID_SIZE * 4;
const NODE_WIDTH = 186;
const NODE_HEIGHT = 114;

const MULTIFUNCTION_RELAY_MODES: RelayMode[] = ['AV', 'RV', 'TI', 'TP', 'IA', 'EW', 'AW', 'ARV', 'ON', 'OFF'];

const DeviceSymbol: React.FC<{ type: CadNodeType; active?: boolean; darkMode: boolean }> = ({ type, active = false, darkMode }) => {
  const stroke = '#111111';
  const fillSoft = '#FFFFFF';
  const activeColor = active ? '#0F766E' : stroke;
  const commonProps = {
    width: 98,
    height: 58,
    viewBox: '0 0 98 58',
    xmlns: 'http://www.w3.org/2000/svg'
  } as const;

  if (type === 'L_SOURCE') {
    return <svg {...commonProps}><line x1="8" y1="29" x2="90" y2="29" stroke={activeColor} strokeWidth="2.6" /><text x="12" y="21" fontSize="12" fontWeight="700" fill={activeColor}>L</text></svg>;
  }
  if (type === 'L2_SOURCE') {
    return <svg {...commonProps}><line x1="8" y1="29" x2="90" y2="29" stroke={activeColor} strokeWidth="2.6" /><text x="8" y="21" fontSize="12" fontWeight="700" fill={activeColor}>L2</text></svg>;
  }
  if (type === 'L3_SOURCE') {
    return <svg {...commonProps}><line x1="8" y1="29" x2="90" y2="29" stroke={activeColor} strokeWidth="2.6" /><text x="8" y="21" fontSize="12" fontWeight="700" fill={activeColor}>L3</text></svg>;
  }
  if (type === 'N_SOURCE') {
    return <svg {...commonProps}><line x1="8" y1="29" x2="90" y2="29" stroke={activeColor} strokeWidth="2.6" /><line x1="14" y1="37" x2="28" y2="37" stroke={activeColor} strokeWidth="2.2" /><line x1="16" y1="41" x2="26" y2="41" stroke={activeColor} strokeWidth="2.2" /></svg>;
  }
  if (type === 'PE_SOURCE') {
    return <svg {...commonProps}><line x1="8" y1="29" x2="48" y2="29" stroke={activeColor} strokeWidth="2.6" /><line x1="48" y1="21" x2="48" y2="37" stroke={activeColor} strokeWidth="2.6" /><line x1="42" y1="37" x2="54" y2="37" stroke={activeColor} strokeWidth="2.2" /><line x1="44" y1="41" x2="52" y2="41" stroke={activeColor} strokeWidth="2.2" /><text x="58" y="24" fontSize="11" fontWeight="700" fill={activeColor}>PE</text></svg>;
  }
  if (type === 'SWITCH_NO' || type === 'TASTER_NO' || type === 'SENSOR_NO') {
    return <svg {...commonProps}><circle cx="16" cy="29" r="4" fill={activeColor} /><circle cx="82" cy="29" r="4" fill={activeColor} /><line x1="21" y1="29" x2={active ? '78' : '60'} y2={active ? '29' : '14'} stroke={activeColor} strokeWidth="2.6" /></svg>;
  }
  if (type === 'FUSE') {
    return <svg {...commonProps}><line x1="8" y1="29" x2="30" y2="29" stroke={activeColor} strokeWidth="2.6" /><rect x="30" y="20" width="38" height="18" fill={fillSoft} stroke={activeColor} strokeWidth="2.4" /><line x1="68" y1="29" x2="90" y2="29" stroke={activeColor} strokeWidth="2.6" /></svg>;
  }
  if (type === 'CROSS_SWITCH') {
    return <svg {...commonProps}><line x1="12" y1="18" x2="86" y2="40" stroke={activeColor} strokeWidth="2.6" /><line x1="12" y1="40" x2="86" y2="18" stroke={activeColor} strokeWidth="2.6" /><circle cx="12" cy="18" r="3.5" fill={activeColor} /><circle cx="12" cy="40" r="3.5" fill={activeColor} /><circle cx="86" cy="18" r="3.5" fill={activeColor} /><circle cx="86" cy="40" r="3.5" fill={activeColor} /></svg>;
  }
  if (type === 'SWITCH_PILOT') {
    return <svg {...commonProps}><circle cx="16" cy="29" r="4" fill={activeColor} /><circle cx="58" cy="29" r="4" fill={activeColor} /><line x1="21" y1="29" x2={active ? '54' : '40'} y2={active ? '29' : '14'} stroke={activeColor} strokeWidth="2.6" /><circle cx="78" cy="29" r="10" fill="none" stroke={active ? '#F59E0B' : stroke} strokeWidth="2.2" /><line x1="71" y1="22" x2="85" y2="36" stroke={active ? '#F59E0B' : stroke} strokeWidth="2" /><line x1="85" y1="22" x2="71" y2="36" stroke={active ? '#F59E0B' : stroke} strokeWidth="2" /></svg>;
  }
  if (type === 'EMERGENCY_STOP_NC') {
    return <svg {...commonProps}><circle cx="24" cy="29" r="12" fill="none" stroke="#DC2626" strokeWidth="2.6" /><line x1="17" y1="22" x2="31" y2="36" stroke="#DC2626" strokeWidth="2.6" /><line x1="40" y1="29" x2="90" y2="29" stroke={activeColor} strokeWidth="2.4" /></svg>;
  }
  if (type === 'OVERLOAD_RELAY') {
    return <svg {...commonProps}><rect x="20" y="14" width="42" height="30" fill="#FFFFFF" stroke={stroke} strokeWidth="2" /><text x="26" y="33" fontSize="10" fontWeight="700" fill={stroke}>OL</text><line x1="8" y1="29" x2="20" y2="29" stroke={activeColor} strokeWidth="2.4" /><line x1="62" y1="29" x2="90" y2="29" stroke={activeColor} strokeWidth="2.4" /></svg>;
  }
  if (type === 'RELAY_COIL') {
    return <svg {...commonProps}><rect x="14" y="12" width="70" height="34" fill="#FFFFFF" stroke={activeColor} strokeWidth="2" /><text x="22" y="24" fontSize="10" fontWeight="700" fill={activeColor}>2xCO</text><line x1="22" y1="33" x2="76" y2="33" stroke={activeColor} strokeWidth="2.2" /></svg>;
  }
  if (type === 'TIMER_RELAY') {
    return <svg {...commonProps}><rect x="14" y="12" width="70" height="34" fill="#FFFFFF" stroke={activeColor} strokeWidth="2" /><text x="28" y="24" fontSize="10" fontWeight="700" fill={activeColor}>TR</text><line x1="22" y1="33" x2="76" y2="33" stroke={activeColor} strokeWidth="2.2" /></svg>;
  }
  if (type === 'MULTIFUNCTION_RELAY') {
    return <svg {...commonProps}><rect x="14" y="12" width="70" height="34" fill="#FFFFFF" stroke={activeColor} strokeWidth="2" /><text x="20" y="24" fontSize="10" fontWeight="700" fill={activeColor}>MFR</text><line x1="22" y1="33" x2="76" y2="33" stroke={activeColor} strokeWidth="2.2" /></svg>;
  }
  if (type === 'CONTACTOR_COIL') {
    return <svg {...commonProps}><line x1="8" y1="29" x2="26" y2="29" stroke={activeColor} strokeWidth="2.6" /><path d="M26 29 C32 13, 40 45, 48 29 C56 13, 64 45, 72 29" fill="none" stroke={activeColor} strokeWidth="2.6" /><line x1="72" y1="29" x2="90" y2="29" stroke={activeColor} strokeWidth="2.6" /></svg>;
  }
  if (type === 'CHANGEOVER') {
    return <svg {...commonProps}><circle cx="30" cy="29" r="4" fill={stroke} /><line x1="30" y1="29" x2="74" y2={active ? '14' : '44'} stroke={stroke} strokeWidth="2.6" /><circle cx="80" cy="14" r="4" fill={stroke} /><circle cx="80" cy="44" r="4" fill={stroke} /></svg>;
  }
  if (type === 'TRANSFORMER') {
    return <svg {...commonProps}><circle cx="33" cy="29" r="10" fill="none" stroke={stroke} strokeWidth="2.2" /><circle cx="62" cy="29" r="10" fill="none" stroke={stroke} strokeWidth="2.2" /><line x1="8" y1="29" x2="22" y2="29" stroke={stroke} strokeWidth="2.4" /><line x1="72" y1="29" x2="90" y2="29" stroke={stroke} strokeWidth="2.4" /><text x="16" y="17" fontSize="8" fontWeight="700" fill={stroke}>230V</text><text x="58" y="17" fontSize="8" fontWeight="700" fill={stroke}>24V</text></svg>;
  }
  if (type === 'SOCKET') {
    return <svg {...commonProps}><circle cx="49" cy="29" r="15" fill="#FFFFFF" stroke={stroke} strokeWidth="2.4" /><circle cx="43" cy="29" r="2.6" fill={active ? '#0F766E' : stroke} /><circle cx="55" cy="29" r="2.6" fill={active ? '#0F766E' : stroke} /><line x1="49" y1="36" x2="49" y2="42" stroke={stroke} strokeWidth="2" /></svg>;
  }
  if (type === 'LAMP') {
    return <svg {...commonProps}><line x1="8" y1="29" x2="26" y2="29" stroke={stroke} strokeWidth="2.6" /><circle cx="49" cy="29" r="18" fill="none" stroke={active ? '#F59E0B' : stroke} strokeWidth="2.4" /><line x1="39" y1="19" x2="59" y2="39" stroke={active ? '#F59E0B' : stroke} strokeWidth="2.4" /><line x1="59" y1="19" x2="39" y2="39" stroke={active ? '#F59E0B' : stroke} strokeWidth="2.4" /><line x1="67" y1="29" x2="90" y2="29" stroke={stroke} strokeWidth="2.6" /></svg>;
  }
  if (type === 'RELAY_4CO') {
    return <svg {...commonProps}><rect x="12" y="10" width="74" height="38" fill="#FFFFFF" stroke={stroke} strokeWidth="2" /><text x="18" y="24" fontSize="10" fontWeight="700" fill={stroke}>4xCO</text><line x1="20" y1="33" x2="78" y2="33" stroke={activeColor} strokeWidth="2.2" /></svg>;
  }
  if (type === 'MOTOR_CONTACTOR') {
    return <svg {...commonProps}><rect x="16" y="10" width="66" height="38" fill="#FFFFFF" stroke={stroke} strokeWidth="2" /><line x1="26" y1="16" x2="26" y2="42" stroke={activeColor} strokeWidth="2.4" /><line x1="49" y1="16" x2="49" y2="42" stroke={activeColor} strokeWidth="2.4" /><line x1="72" y1="16" x2="72" y2="42" stroke={activeColor} strokeWidth="2.4" /><text x="19" y="24" fontSize="8" fontWeight="700" fill={stroke}>AUX</text></svg>;
  }
  if (type === 'DIMMER') {
    return <svg {...commonProps}><rect x="18" y="12" width="62" height="34" fill="#FFFFFF" stroke={stroke} strokeWidth="2" /><path d="M26 35 L34 23 L42 35 L50 23 L58 35 L66 23" fill="none" stroke={activeColor} strokeWidth="2" /></svg>;
  }
  if (type === 'BUZZER') {
    return <svg {...commonProps}><line x1="8" y1="29" x2="28" y2="29" stroke={stroke} strokeWidth="2.6" /><polygon points="28,18 52,18 66,29 52,40 28,40" fill="none" stroke={activeColor} strokeWidth="2.2" /><line x1="70" y1="24" x2="82" y2="18" stroke={activeColor} strokeWidth="2" /><line x1="70" y1="34" x2="84" y2="40" stroke={activeColor} strokeWidth="2" /></svg>;
  }
  if (type === 'BLINKER') {
    return <svg {...commonProps}><rect x="18" y="12" width="62" height="34" fill="#FFFFFF" stroke={stroke} strokeWidth="2" /><circle cx="49" cy="29" r="9" fill={active ? '#F59E0B' : '#FFFFFF'} stroke={stroke} strokeWidth="2" /></svg>;
  }
  if (type === 'MOTOR_AC') {
    return <svg {...commonProps}><circle cx="49" cy="29" r="17" fill="none" stroke={active ? '#0F766E' : stroke} strokeWidth="2.6" /><text x="42" y="33" fontSize="12" fontWeight="700" fill={active ? '#0F766E' : stroke}>M~</text><line x1="8" y1="29" x2="32" y2="29" stroke={stroke} strokeWidth="2.4" /><line x1="66" y1="29" x2="90" y2="29" stroke={stroke} strokeWidth="2.4" /></svg>;
  }
  if (type === 'MOTOR_3PH') {
    return <svg {...commonProps}><circle cx="49" cy="29" r="17" fill="none" stroke={active ? '#0F766E' : stroke} strokeWidth="2.6" /><text x="38" y="33" fontSize="11" fontWeight="700" fill={active ? '#0F766E' : stroke}>M3~</text><line x1="8" y1="22" x2="32" y2="22" stroke={stroke} strokeWidth="2.4" /><line x1="8" y1="29" x2="32" y2="29" stroke={stroke} strokeWidth="2.4" /><line x1="8" y1="36" x2="32" y2="36" stroke={stroke} strokeWidth="2.4" /></svg>;
  }
  if (type === 'JUNCTION_BOX') {
    return <svg {...commonProps}><rect x="10" y="8" width="78" height="42" fill="#FFFFFF" stroke={stroke} strokeWidth="2" /></svg>;
  }
  if (type === 'JUNCTION') {
    return <svg {...commonProps}><rect x="32" y="16" width="34" height="26" fill="#FFFFFF" stroke={stroke} strokeWidth="2" /><circle cx="49" cy="29" r="4" fill={activeColor} /></svg>;
  }
  return <svg {...commonProps}><line x1="8" y1="29" x2="90" y2="29" stroke={stroke} strokeWidth="2.6" /><rect x="37" y="21" width="24" height="16" fill={fillSoft} stroke={stroke} strokeWidth="2" /></svg>;
};

const NODE_DEFINITIONS: Record<CadNodeType, NodeDefinition> = {
  L_SOURCE: {
    label: 'Phase L1',
    inputs: [],
    outputs: ['L'],
    toggleable: true,
    description: 'Phasenquelle L1.'
  },
  L2_SOURCE: {
    label: 'Phase L2',
    inputs: [],
    outputs: ['L2'],
    toggleable: true,
    description: 'Phasenquelle L2.'
  },
  L3_SOURCE: {
    label: 'Phase L3',
    inputs: [],
    outputs: ['L3'],
    toggleable: true,
    description: 'Phasenquelle L3.'
  },
  N_SOURCE: {
    label: 'Neutralleiter N',
    inputs: [],
    outputs: ['N'],
    toggleable: true,
    description: 'Neutralleiter/Masse (N).'
  },
  PE_SOURCE: {
    label: 'Schutzleiter PE',
    inputs: [],
    outputs: ['PE'],
    toggleable: true,
    description: 'Schutzleiter (Erdung/PE).'
  },
  SWITCH_NO: {
    label: 'Schalter (NO)',
    inputs: ['A', 'B'],
    outputs: ['A', 'B'],
    toggleable: true,
    description: '2-Klemmen-Schalter (A/B), von beiden Seiten belegbar.'
  },
  CROSS_SWITCH: {
    label: 'Kreuzschalter',
    inputs: ['A', 'B'],
    outputs: ['A_OUT', 'B_OUT'],
    toggleable: true,
    description: 'Kreuzt zwei korrespondierende Leiter; Zustand EIN tauscht A/B, AUS führt gerade durch.'
  },
  TASTER_NO: {
    label: 'Taster (NO)',
    inputs: ['A', 'B'],
    outputs: ['A', 'B'],
    toggleable: true,
    description: '2-Klemmen-Taster (A/B), von beiden Seiten belegbar.'
  },
  SENSOR_NO: {
    label: 'Sensor (NO)',
    inputs: ['A', 'B'],
    outputs: ['A', 'B'],
    toggleable: true,
    description: '2-Klemmen-Sensor (A/B), von beiden Seiten belegbar.'
  },
  SWITCH_PILOT: {
    label: 'Schalter + Meldeleuchte',
    inputs: ['L', 'N'],
    outputs: ['OUT', 'LAMP_ON'],
    toggleable: true,
    description: 'Bedien-Schalter mit integrierter Meldeleuchte; zeigt Schaltzustand lokal an.'
  },
  FUSE: {
    label: 'Sicherung',
    inputs: ['A', 'B'],
    outputs: ['A', 'B'],
    toggleable: true,
    description: '2-Klemmen-Sicherung (A/B), von beiden Seiten belegbar.'
  },
  RELAY_COIL: {
    label: 'Relais 2x Wechsler',
    inputs: ['A1', 'A2', 'COM1', 'COM2'],
    outputs: ['NO1', 'NC1', 'NO2', 'NC2'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Vollwertiges Relais mit A1/A2-Spule und 2 Wechslern; Spulenspannung 24V/230V wählbar.'
  },
  TIMER_RELAY: {
    label: 'Zeitrelais 2x Wechsler',
    inputs: ['A1', 'A2', 'COM1', 'COM2'],
    outputs: ['NO1', 'NC1', 'NO2', 'NC2'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Zeitrelais mit einstellbarer Zeit (ms) und Modus (ANZUG/ABFALL/IMPULS), 2 Wechsler.'
  },
  MULTIFUNCTION_RELAY: {
    label: 'Multifunktionsrelais 2x Wechsler',
    inputs: ['A1', 'A2', 'COM1', 'COM2'],
    outputs: ['NO1', 'NC1', 'NO2', 'NC2'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Multifunktionsrelais mit 10 Modi (AV/RV/TI/TP/IA/EW/AW/ARV/ON/OFF) und einstellbarer Zeit.'
  },
  RELAY_4CO: {
    label: 'Relais 4x Wechsler',
    inputs: ['A1', 'A2', 'COM1', 'COM2', 'COM3', 'COM4'],
    outputs: ['NO1', 'NC1', 'NO2', 'NC2', 'NO3', 'NC3', 'NO4', 'NC4'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Relais mit Spule A1/A2 und vier potenzialfreien Wechslern; Spule 24V/230V wählbar.'
  },
  CONTACTOR_COIL: {
    label: 'Schütz-Spule',
    inputs: ['A1', 'A2'],
    outputs: ['K'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Schützspule für Leistungsschütz; schaltet Hauptkontakte indirekt. Spulenspannung 24V/230V wählbar.'
  },
  MOTOR_CONTACTOR: {
    label: 'Motor-Schütz',
    inputs: ['A1', 'A2', 'L1', 'L2', 'L3', 'AUX_COM'],
    outputs: ['T1', 'T2', 'T3', 'AUX_NO', 'AUX_NC'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Dreipoliger Leistungsschütz mit A1/A2-Spule und einem Hilfswechsler (AUX_COM/AUX_NO/AUX_NC).'
  },
  MOTOR_AC: {
    label: 'Wechselstrommotor',
    inputs: ['L', 'N', 'PE'],
    outputs: ['RUN'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Einphasenmotor; Nennspannung 24V/230V wählbar. Überspannung führt zum Durchbrennen.'
  },
  MOTOR_3PH: {
    label: 'Drehstrommotor',
    inputs: ['U', 'V', 'W', 'PE'],
    outputs: ['RUN'],
    description: 'Drehstrommotor; Drehrichtung ergibt sich aus der Phasenfolge auf U/V/W (vertauschte Phasen = Richtungswechsel).'
  },
  EMERGENCY_STOP_NC: {
    label: 'Not-Aus (NC)',
    inputs: ['A', 'B'],
    outputs: ['A', 'B'],
    toggleable: true,
    description: '2-Klemmen-Öffner (A/B), von beiden Seiten belegbar.'
  },
  OVERLOAD_RELAY: {
    label: 'Motorschutzrelais 4-polig',
    inputs: ['A1', 'A2', 'L1', 'L2', 'L3', 'N', 'COM'],
    outputs: ['T1', 'T2', 'T3', 'TN', 'AUX_NO', 'AUX_NC'],
    toggleable: true,
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: '4-poliger Motorschutz mit A1/A2-Steuerung und Hilfskontakt (COM/AUX_NO/AUX_NC).'
  },
  DIMMER: {
    label: 'Dimmer',
    inputs: ['L', 'N'],
    outputs: ['OUT'],
    toggleable: true,
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Dimmer 0–100%. Regelt Helligkeit/Drehzahl; Relaisfunktionen fallen unter 80% ab.'
  },
  BUZZER: {
    label: 'Summer',
    inputs: ['L', 'N'],
    outputs: ['ON'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Akustischer Signalgeber; aktiv bei passender Betriebsspannung.'
  },
  BLINKER: {
    label: 'Blinkgeber',
    inputs: ['L', 'N'],
    outputs: ['OUT'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Blinkgeber mit einstellbarer Periodendauer (ms).'
  },
  CHANGEOVER: {
    label: 'Wechselschalter',
    inputs: ['L'],
    outputs: ['NO', 'NC'],
    toggleable: true,
    description: 'Umschalter für Wechsel-/Kreuzlogiken; Stellung per Schalt-Button NO/NC.'
  },
  TRANSFORMER: {
    label: 'Trafo',
    inputs: ['PRI_L', 'PRI_N'],
    outputs: ['SEC_L', 'SEC_N'],
    description: 'Trafo 230V -> 24V: Sekundärseite liefert 24V bei primärer Versorgung.'
  },
  SOCKET: {
    label: 'Steckdose',
    inputs: ['L', 'N', 'PE'],
    outputs: ['READY'],
    description: 'Schutzkontakt-Steckdose als Anschlussgerät; READY bei korrekter Versorgung.'
  },
  LAMP: {
    label: 'Lampe',
    inputs: ['L', 'N'],
    outputs: ['ON'],
    supportsVoltageSelection: true,
    defaultVoltage: 230,
    description: 'Lampe mit wählbarer Nennspannung (24V/230V). Übernennspannung: Lampe brennt durch.'
  },
  JUNCTION: {
    label: 'Klemmpunkt',
    inputs: ['IN'],
    outputs: ['OUT'],
    description: 'Punktförmiger Klemmpunkt im Rahmen für saubere Potenzialführung.'
  },
  JUNCTION_BOX: {
    label: 'Abzweigdose',
    inputs: [],
    outputs: [],
    description: 'Leerer Rahmen als Abzweigdose. Klemmpunkte werden separat hineingelegt.'
  }
};

const TASKS: CadTask[] = [
  {
    title: '1) Ausschaltung',
    description: 'Einfache Ausschaltung: L1 über S1 auf H1, Rückleiter auf N.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'SWITCH_NO', 'LAMP'],
    requiredLabels: ['S1', 'H1'],
    vectors: [
      { inputs: { S1: false }, expected: { H1: false } },
      { inputs: { S1: true }, expected: { H1: true } }
    ]
  },
  {
    title: '2) Treppenhausschaltung (Taster + Zeitrelais)',
    description: 'T1 triggert KT1, H1 leuchtet zeitbegrenzt über NO-Kontakt des Zeitrelais.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'TASTER_NO', 'TIMER_RELAY', 'LAMP'],
    requiredLabels: ['T1', 'KT1', 'H1'],
    vectors: [
      { inputs: { T1: false }, expected: { H1: false } },
      { inputs: { T1: true }, expected: { H1: true } }
    ]
  },
  {
    title: '3) Wechselschaltung',
    description: 'Zwei Schaltstellen für eine Lampe, funktional als XOR.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'SWITCH_NO', 'CHANGEOVER', 'LAMP'],
    requiredLabels: ['S1', 'S2', 'H1'],
    vectors: [
      { inputs: { S1: false, S2: false }, expected: { H1: false } },
      { inputs: { S1: true, S2: false }, expected: { H1: true } },
      { inputs: { S1: false, S2: true }, expected: { H1: true } },
      { inputs: { S1: true, S2: true }, expected: { H1: false } }
    ]
  },
  {
    title: '4) Sparwechselschaltung',
    description: 'H1 soll bei S1 ODER S2 EIN werden.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'SWITCH_NO', 'LAMP'],
    requiredLabels: ['S1', 'S2', 'H1', 'XS1'],
    vectors: [
      { inputs: { S1: false, S2: false }, expected: { H1: false } },
      { inputs: { S1: true, S2: false }, expected: { H1: true } },
      { inputs: { S1: false, S2: true }, expected: { H1: true } }
    ]
  },
  {
    title: '5) Kreuzschaltung',
    description: 'Drei Schaltstellen mit Kreuzschalter für H1.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'SWITCH_NO', 'CROSS_SWITCH', 'LAMP'],
    requiredLabels: ['S1', 'S2', 'S3', 'H1'],
    vectors: [
      { inputs: { S1: false, S2: false, S3: false }, expected: { H1: false } },
      { inputs: { S1: true, S2: false, S3: false }, expected: { H1: true } },
      { inputs: { S1: true, S2: true, S3: false }, expected: { H1: false } },
      { inputs: { S1: true, S2: true, S3: true }, expected: { H1: true } }
    ]
  },
  {
    title: '6) Steckdose schaltbar',
    description: 'S1 schaltet eine Steckdose XS1, korrekt mit L/N/PE.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'PE_SOURCE', 'SWITCH_NO', 'SOCKET'],
    requiredLabels: ['S1', 'XS1'],
    vectors: [
      { inputs: { S1: false }, expected: { XS1: false } },
      { inputs: { S1: true }, expected: { XS1: true } }
    ]
  },
  {
    title: '7) Steckdose + Lampe getrennt',
    description: 'S1 schaltet H1, S2 schaltet XS1.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'PE_SOURCE', 'SWITCH_NO', 'LAMP', 'SOCKET'],
    requiredLabels: ['S1', 'S2', 'H1', 'XS1'],
    vectors: [
      { inputs: { S1: false, S2: false }, expected: { H1: false, XS1: false } },
      { inputs: { S1: true, S2: false }, expected: { H1: true, XS1: false } },
      { inputs: { S1: false, S2: true }, expected: { H1: false, XS1: true } }
    ]
  },
  {
    title: '8) Dimmer-Licht',
    description: 'D1 regelt H1. Unter 1% bleibt H1 AUS.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'DIMMER', 'LAMP'],
    requiredLabels: ['D1', 'H1'],
    vectors: [
      { inputs: { D1: false }, expected: { H1: false } },
      { inputs: { D1: true }, expected: { H1: true } }
    ]
  },
  {
    title: '9) Taster-Licht',
    description: 'H1 darf nur während gedrücktem Taster T1 versorgt werden.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'TASTER_NO', 'LAMP'],
    requiredLabels: ['T1', 'H1'],
    vectors: [
      { inputs: { T1: false }, expected: { H1: false } },
      { inputs: { T1: true }, expected: { H1: true } }
    ]
  },
  {
    title: '10) Steckdose abgesichert',
    description: 'F1 in Serie zur Steckdose XS1; Versorgung nur bei aktiver Sicherung.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'PE_SOURCE', 'FUSE', 'SOCKET'],
    requiredLabels: ['F1', 'XS1'],
    vectors: [
      { inputs: { F1: false }, expected: { XS1: false } },
      { inputs: { F1: true }, expected: { XS1: true } }
    ]
  },
  {
    title: '11) Stern-Dreieck (Grundaufbau)',
    description: 'KM1 als Hauptschütz plus Umschaltung Stern/Dreieck über Zeitglied.',
    requiredTypes: ['L_SOURCE', 'L2_SOURCE', 'L3_SOURCE', 'N_SOURCE', 'MOTOR_3PH', 'MOTOR_CONTACTOR', 'TIMER_RELAY'],
    requiredLabels: ['KM1', 'KT1', 'M31'],
    vectors: [
      { inputs: { KT1: false }, expected: { M31: false } },
      { inputs: { KT1: true }, expected: { M31: true } }
    ]
  },
  {
    title: '12) Wendeschütz',
    description: 'S1 = Vorwärts, S2 = Rückwärts, gegenseitige Verriegelung über Hilfskontakte.',
    requiredTypes: ['L_SOURCE', 'L2_SOURCE', 'L3_SOURCE', 'N_SOURCE', 'SWITCH_NO', 'MOTOR_CONTACTOR', 'MOTOR_3PH'],
    requiredLabels: ['S1', 'S2', 'Q1', 'Q2', 'M31'],
    vectors: [
      { inputs: { S1: false, S2: false }, expected: { M31: false } },
      { inputs: { S1: true, S2: false }, expected: { M31: true } },
      { inputs: { S1: true, S2: true }, expected: { M31: false } }
    ]
  },
  {
    title: '13) 24V-Steuerung / 230V-Last',
    description: 'Trafo erzeugt 24V-Steuerkreis für K1; K1 schaltet 230V-Lampe H1.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'TRANSFORMER', 'RELAY_COIL', 'LAMP'],
    requiredLabels: ['TR1', 'K1', 'H1'],
    vectors: [
      { inputs: { K1: false }, expected: { H1: false } },
      { inputs: { K1: true }, expected: { H1: true } }
    ]
  },
  {
    title: '14) Motorschutz + Schütz',
    description: 'FR1 überwacht den Motorpfad und meldet über AUX.',
    requiredTypes: ['L_SOURCE', 'L2_SOURCE', 'L3_SOURCE', 'N_SOURCE', 'MOTOR_CONTACTOR', 'OVERLOAD_RELAY', 'MOTOR_3PH'],
    requiredLabels: ['FR1', 'Q1', 'M31'],
    vectors: [
      { inputs: { FR1: false }, expected: { M31: false } },
      { inputs: { FR1: true }, expected: { M31: true } }
    ]
  },
  {
    title: '15) MFR als Treppenlicht',
    description: 'KMF1 im Modus AV/RV, Tasterstart für H1.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'TASTER_NO', 'MULTIFUNCTION_RELAY', 'LAMP'],
    requiredLabels: ['T1', 'KMF1', 'H1'],
    vectors: [
      { inputs: { T1: false }, expected: { H1: false } },
      { inputs: { T1: true }, expected: { H1: true } }
    ]
  },
  {
    title: '16) Wende + 24V Steuerkreis',
    description: 'Richtungswahl im 24V-Kreis, Leistungsteil 230V/3~ über Schütze.',
    requiredTypes: ['L_SOURCE', 'L2_SOURCE', 'L3_SOURCE', 'N_SOURCE', 'TRANSFORMER', 'MOTOR_CONTACTOR', 'MOTOR_3PH'],
    requiredLabels: ['TR1', 'Q1', 'Q2', 'M31'],
    vectors: [
      { inputs: { Q1: false, Q2: false }, expected: { M31: false } },
      { inputs: { Q1: true, Q2: false }, expected: { M31: true } }
    ]
  },
  {
    title: '17) Stern-Dreieck mit Meldung',
    description: 'Zusätzliche Meldelampe zeigt Betriebszustand des Leistungsteils.',
    requiredTypes: ['L_SOURCE', 'L2_SOURCE', 'L3_SOURCE', 'N_SOURCE', 'MOTOR_3PH', 'MOTOR_CONTACTOR', 'TIMER_RELAY', 'LAMP'],
    requiredLabels: ['KM1', 'KT1', 'M31', 'H1'],
    vectors: [
      { inputs: { KT1: false }, expected: { H1: false } },
      { inputs: { KT1: true }, expected: { H1: true } }
    ]
  },
  {
    title: '18) Dimmer-Motor + Relaisgrenze',
    description: 'Dimmer regelt Motor; Relaisansteuerung bricht unter 80% weg.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'DIMMER', 'MOTOR_AC', 'RELAY_COIL'],
    requiredLabels: ['D1', 'M1', 'K1'],
    vectors: [
      { inputs: { D1: false }, expected: { M1: false } },
      { inputs: { D1: true }, expected: { M1: true } }
    ]
  },
  {
    title: '19) Kombi 24V/230V mit Steckdose',
    description: '24V steuert Kette, 230V versorgt H1 und XS1 über Freigabekontakt.',
    requiredTypes: ['L_SOURCE', 'N_SOURCE', 'PE_SOURCE', 'TRANSFORMER', 'RELAY_COIL', 'LAMP', 'SOCKET'],
    requiredLabels: ['TR1', 'K1', 'H1', 'XS1'],
    vectors: [
      { inputs: { K1: false }, expected: { H1: false, XS1: false } },
      { inputs: { K1: true }, expected: { H1: true, XS1: true } }
    ]
  },
  {
    title: '20) Komplexe Kombinationsschaltung',
    description: 'Freie Abschlussaufgabe: Wende-/Zeit-/24V+230V-Kombination mit sauberer PE/N-Führung.',
    requiredTypes: ['L_SOURCE', 'L2_SOURCE', 'L3_SOURCE', 'N_SOURCE', 'PE_SOURCE', 'TRANSFORMER', 'MULTIFUNCTION_RELAY', 'MOTOR_CONTACTOR', 'MOTOR_3PH', 'SOCKET', 'LAMP'],
    requiredLabels: ['TR1', 'KMF1', 'Q1', 'M31', 'XS1', 'H1'],
    vectors: [
      { inputs: { KMF1: false }, expected: { H1: false } },
      { inputs: { KMF1: true }, expected: { H1: true } }
    ]
  }
];

const initialSnackbar: SnackbarState = {
  open: false,
  severity: 'info',
  message: ''
};

const M10_U2_X2_CAD: React.FC = () => {
  const { darkMode } = useTheme();
  const {
    totalScore,
    token,
    canViewM10Cad,
    canEditM10CadAccess,
    canEditM10CadComponents,
    canEditM10CadConnections,
    canUseM10CadSimulation,
    canSaveM10CadProjects,
    canValidateM10CadTasks
  } = useAuth();

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const rawRequired = Number(override?.design?.requiredLevel);
  const currentRequired = Number.isFinite(rawRequired) ? rawRequired : 1000;
  const hasCadPrivilege =
    canViewM10Cad
    || canEditM10CadAccess
    || canEditM10CadComponents
    || canEditM10CadConnections
    || canUseM10CadSimulation
    || canSaveM10CadProjects
    || canValidateM10CadTasks;
  const [requiredLevel, setRequiredLevel] = React.useState<string>(String(currentRequired));
  const [accessDialogOpen, setAccessDialogOpen] = React.useState(false);
  const [submittingAccess, setSubmittingAccess] = React.useState(false);
  const [accessError, setAccessError] = React.useState<string | null>(null);
  const [accessSuccess, setAccessSuccess] = React.useState<string | null>(null);

  const [selectedType, setSelectedType] = React.useState<CadNodeType>('SWITCH_NO');
  const [nodes, setNodes] = React.useState<CadNode[]>([]);
  const [selectedNodeId, setSelectedNodeId] = React.useState<string | null>(null);
  const [connections, setConnections] = React.useState<Connection[]>([]);
  const [selectedPinId, setSelectedPinId] = React.useState<string | null>(null);
  const [routeMode, setRouteMode] = React.useState<RouteMode>('DIRECT');
  const [draftRoutePoints, setDraftRoutePoints] = React.useState<{ x: number; y: number }[]>([]);
  const [selectedTaskIndex, setSelectedTaskIndex] = React.useState(0);
  const [isSimulating, setIsSimulating] = React.useState(false);
  const [livePinValues, setLivePinValues] = React.useState<Record<string, boolean>>({});
  const [motorStates, setMotorStates] = React.useState<Record<string, MotorVisualState>>({});
  const [snackbar, setSnackbar] = React.useState<SnackbarState>(initialSnackbar);
  const [validationOpen, setValidationOpen] = React.useState(false);
  const [validationResults, setValidationResults] = React.useState<ValidationResult[]>([]);
  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [dragState, setDragState] = React.useState<DragState | null>(null);
  const [blockScale, setBlockScale] = React.useState(0.74);

  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const canvasRef = React.useRef<HTMLDivElement | null>(null);
  const legendScrollRef = React.useRef<HTMLDivElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const pinRefs = React.useRef<Record<string, HTMLElement | null>>({});
  const pinRecalcFrameRef = React.useRef<number | null>(null);
  const notifiedBurnedNodesRef = React.useRef<Set<string>>(new Set());
  const lastShortCircuitRef = React.useRef<string>('');
  const relayTimingRef = React.useRef<Record<string, { lastCoilOn: boolean; onSince?: number; offSince?: number; pulseUntil?: number; cycleSince?: number }>>({});
  const [pinPositions, setPinPositions] = React.useState<Record<string, { x: number; y: number }>>({});

  const snapToGrid = React.useCallback((value: number): number => {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
  }, []);

  const scaledNodeWidth = Math.max(132, Math.round(NODE_WIDTH * blockScale));
  const scaledNodeHeight = Math.max(72, Math.round(NODE_HEIGHT * blockScale));
  const scaledJunctionSize = Math.max(GRID_SIZE, Math.round(GRID_SIZE * 2 * blockScale));
  const scaledJunctionBoxWidth = Math.max(GRID_SIZE * 8, Math.round(GRID_SIZE * 12 * blockScale));
  const scaledJunctionBoxHeight = Math.max(GRID_SIZE * 5, Math.round(GRID_SIZE * 8 * blockScale));
  const basePinSlotWidth = Math.max(22, Math.round(26 * blockScale));
  const maxPinSlotWidth = Math.max(30, Math.round(34 * blockScale));
  const standardNodePaddingX = Math.max(12, Math.round(18 * blockScale));
  const maxPinsPerSide = React.useMemo(() => {
    return Object.values(NODE_DEFINITIONS).reduce((max, definition) => {
      return Math.max(max, definition.inputs.length, definition.outputs.length);
    }, 1);
  }, []);
  const estimatedMaxPinSlotWidth = maxPinSlotWidth;
  const maxStandardNodeWidth = Math.max(scaledNodeWidth, maxPinsPerSide * estimatedMaxPinSlotWidth + standardNodePaddingX * 2);
  const scaledGridXStep = Math.max(maxStandardNodeWidth + GRID_SIZE * 2, Math.round(GRID_X_STEP * blockScale));
  const scaledGridYStep = Math.max(GRID_SIZE * 5, Math.round(GRID_Y_STEP * blockScale));
  const blockSymbolScale = Math.max(0.62, blockScale);
  const legendDeviceTypes = React.useMemo(() => {
    const switchTypes = new Set<CadNodeType>(['SWITCH_NO', 'CROSS_SWITCH', 'TASTER_NO', 'SENSOR_NO', 'SWITCH_PILOT', 'EMERGENCY_STOP_NC', 'CHANGEOVER']);
    const types = Object.keys(NODE_DEFINITIONS) as CadNodeType[];
    return [...types].sort((leftType, rightType) => {
      const leftIsSwitch = switchTypes.has(leftType);
      const rightIsSwitch = switchTypes.has(rightType);
      if (leftIsSwitch !== rightIsSwitch) {
        return leftIsSwitch ? -1 : 1;
      }
      return NODE_DEFINITIONS[leftType].label.localeCompare(NODE_DEFINITIONS[rightType].label, 'de', { sensitivity: 'base' });
    });
  }, []);
  const legendLoopTypes = React.useMemo(
    () => [...legendDeviceTypes, ...legendDeviceTypes, ...legendDeviceTypes],
    [legendDeviceTypes]
  );

  const getNodePinSlotWidth = React.useCallback((node: Pick<CadNode, 'inputs' | 'outputs'>): number => {
    const longestPinNameLength = Math.max(
      ...[...node.inputs, ...node.outputs].map((pin) => pin.name.length),
      2
    );

    if (longestPinNameLength <= 3) return basePinSlotWidth;
    if (longestPinNameLength <= 5) return Math.min(maxPinSlotWidth, basePinSlotWidth + 4);
    return maxPinSlotWidth;
  }, [basePinSlotWidth, maxPinSlotWidth]);

  const getNodeDimensions = React.useCallback((node: Pick<CadNode, 'type' | 'inputs' | 'outputs'>): { width: number; height: number; pinSlotWidth: number } => {
    if (node.type === 'JUNCTION') {
      return {
        width: scaledJunctionSize,
        height: scaledJunctionSize,
        pinSlotWidth: scaledJunctionSize
      };
    }

    if (node.type === 'JUNCTION_BOX') {
      return {
        width: scaledJunctionBoxWidth,
        height: scaledJunctionBoxHeight,
        pinSlotWidth: 0
      };
    }

    const pinSlotWidth = getNodePinSlotWidth(node);
    const pinsPerSide = Math.max(node.inputs.length, node.outputs.length, 1);
    const requiredPinRowWidth = pinsPerSide * pinSlotWidth + standardNodePaddingX * 2;

    return {
      width: Math.max(scaledNodeWidth, requiredPinRowWidth),
      height: scaledNodeHeight,
      pinSlotWidth
    };
  }, [getNodePinSlotWidth, scaledJunctionBoxHeight, scaledJunctionBoxWidth, scaledJunctionSize, scaledNodeHeight, scaledNodeWidth, standardNodePaddingX]);

  const getGridPositionForIndex = React.useCallback((index: number): { x: number; y: number } => {
    const viewportWidth = viewportRef.current?.clientWidth ?? 0;
    const columnsPerRow = viewportWidth > 0
      ? Math.min(6, Math.max(2, Math.floor((viewportWidth - GRID_START_X * 2) / scaledGridXStep)))
      : 6;
    const col = index % columnsPerRow;
    const row = Math.floor(index / columnsPerRow);
    return {
      x: snapToGrid(GRID_START_X + col * scaledGridXStep),
      y: snapToGrid(GRID_START_Y + row * scaledGridYStep)
    };
  }, [scaledGridXStep, scaledGridYStep, snapToGrid]);

  React.useEffect(() => {
    setRequiredLevel(String(Number(override?.design?.requiredLevel ?? 1000)));
  }, [override?.design?.requiredLevel]);

  const showSnackbar = React.useCallback((message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  React.useEffect(() => {
    const nodesNeedingNormalization = nodes.filter((node) => {
      if (node.type !== 'CHANGEOVER') return false;
      const hasLInput = node.inputs.some((pin) => pin.name === 'L');
      const hasExtraInputs = node.inputs.some((pin) => pin.name !== 'L');
      const hasToggleState = typeof node.state.active === 'boolean';
      return !hasLInput || hasExtraInputs || !hasToggleState;
    });

    if (nodesNeedingNormalization.length === 0) return;

    const removedPinIds = nodesNeedingNormalization.flatMap((node) => node.inputs.filter((pin) => pin.name !== 'L').map((pin) => pin.id));

    setNodes((previousNodes) => previousNodes.map((node) => {
      if (node.type !== 'CHANGEOVER') return node;

      const lInput = node.inputs.find((pin) => pin.name === 'L');
      const normalizedLInput = lInput ?? {
        id: createId(),
        nodeId: node.id,
        isInput: true,
        name: 'L',
        signalType: 'BOOL' as SignalType
      };

      return {
        ...node,
        inputs: [normalizedLInput],
        state: {
          ...node.state,
          active: Boolean(node.state.active ?? false)
        }
      };
    }));

    if (removedPinIds.length > 0) {
      setConnections((previousConnections) => previousConnections.filter((connection) => !removedPinIds.includes(connection.fromPinId) && !removedPinIds.includes(connection.toPinId)));
      setSelectedPinId((previousPinId) => (previousPinId && removedPinIds.includes(previousPinId) ? null : previousPinId));
    }
  }, [nodes]);

  const handleLegendScroll = React.useCallback(() => {
    const container = legendScrollRef.current;
    if (!container) return;

    const segmentHeight = container.scrollHeight / 3;
    if (segmentHeight <= 0) return;

    if (container.scrollTop < segmentHeight * 0.5) {
      container.scrollTop += segmentHeight;
      return;
    }

    if (container.scrollTop > segmentHeight * 1.5) {
      container.scrollTop -= segmentHeight;
    }
  }, []);

  React.useEffect(() => {
    const initializeLegendLoop = () => {
      const container = legendScrollRef.current;
      if (!container) return;
      const segmentHeight = container.scrollHeight / 3;
      if (segmentHeight > 0) {
        container.scrollTop = segmentHeight;
      }
    };

    const run = () => {
      window.requestAnimationFrame(() => {
        initializeLegendLoop();
      });
    };

    run();
    const timeout = window.setTimeout(run, 80);
    window.addEventListener('resize', run);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('resize', run);
    };
  }, [legendLoopTypes.length]);

  const recalculateBlockScale = React.useCallback(() => {
    if (typeof window === 'undefined') return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const diagonalPx = Math.hypot(viewportWidth, viewportHeight);
    const isLandscape = viewportWidth >= viewportHeight;

    let nextScale = 0.74;
    if (isLandscape) {
      if (diagonalPx < 1100) nextScale = 0.58;
      else if (diagonalPx < 1500) nextScale = 0.64;
      else if (diagonalPx < 2100) nextScale = 0.72;
      else nextScale = 0.78;
    } else {
      if (diagonalPx < 900) nextScale = 0.52;
      else if (diagonalPx < 1200) nextScale = 0.58;
      else if (diagonalPx < 1600) nextScale = 0.66;
      else nextScale = 0.72;
    }

    setBlockScale((previous) => (Math.abs(previous - nextScale) > 0.01 ? Number(nextScale.toFixed(2)) : previous));
  }, []);

  React.useEffect(() => {
    const run = () => {
      window.requestAnimationFrame(() => {
        recalculateBlockScale();
      });
    };

    run();
    window.addEventListener('resize', run);
    window.addEventListener('orientationchange', run);
    return () => {
      window.removeEventListener('resize', run);
      window.removeEventListener('orientationchange', run);
    };
  }, [recalculateBlockScale]);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      recalculateBlockScale();
    }, 40);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [nodes.length, selectedTaskIndex, recalculateBlockScale]);

  const getNodeTypeCounter = React.useCallback((nodeType: CadNodeType, existingNodes: CadNode[]): number => {
    return existingNodes.filter((node) => node.type === nodeType).length + 1;
  }, []);

  const getLabelPrefix = React.useCallback((nodeType: CadNodeType): string => {
    const map: Record<CadNodeType, string> = {
      L_SOURCE: 'L',
      L2_SOURCE: 'L2',
      L3_SOURCE: 'L3',
      N_SOURCE: 'N',
      PE_SOURCE: 'PE',
      SWITCH_NO: 'S',
      CROSS_SWITCH: 'KS',
      TASTER_NO: 'T',
      SENSOR_NO: 'B',
      SWITCH_PILOT: 'SM',
      FUSE: 'F',
      RELAY_COIL: 'K',
      RELAY_4CO: 'KW',
      TIMER_RELAY: 'KT',
      MULTIFUNCTION_RELAY: 'KMF',
      CONTACTOR_COIL: 'KM',
      MOTOR_CONTACTOR: 'Q',
      MOTOR_AC: 'M',
      MOTOR_3PH: 'M3',
      EMERGENCY_STOP_NC: 'S0',
      OVERLOAD_RELAY: 'FR',
      DIMMER: 'D',
      BUZZER: 'BZ',
      BLINKER: 'BL',
      CHANGEOVER: 'W',
      TRANSFORMER: 'TR',
      SOCKET: 'XS',
      LAMP: 'H',
      JUNCTION: 'X',
      JUNCTION_BOX: 'AD'
    };
    return map[nodeType];
  }, []);

  const buildNode = React.useCallback((nodeType: CadNodeType, index: number, existingNodes: CadNode[]): CadNode => {
    const nodeDef = NODE_DEFINITIONS[nodeType];
    const count = getNodeTypeCounter(nodeType, existingNodes);
    const label = `${getLabelPrefix(nodeType)}${count}`;
    const displayName = nodeType === 'JUNCTION' ? 'Klemmpunkt' : nodeType === 'JUNCTION_BOX' ? `Abzweigdose ${count}` : '';
    const { x, y } = getGridPositionForIndex(index);
    const nodeId = createId();

    return {
      id: nodeId,
      type: nodeType,
      label,
      displayName,
      x,
      y,
      inputs: nodeDef.inputs.map((inputName) => ({
        id: createId(),
        nodeId,
        isInput: true,
        name: inputName,
        signalType: 'BOOL'
      })),
      outputs: nodeDef.outputs.map((outputName) => ({
        id: createId(),
        nodeId,
        isInput: false,
        name: outputName,
        signalType: 'BOOL'
      })),
      state: {
        active: nodeDef.toggleable
          ? (
            nodeType === 'L_SOURCE'
            || nodeType === 'L2_SOURCE'
            || nodeType === 'L3_SOURCE'
            || nodeType === 'N_SOURCE'
            || nodeType === 'PE_SOURCE'
            || nodeType === 'EMERGENCY_STOP_NC'
            || nodeType === 'OVERLOAD_RELAY'
          )
          : undefined,
        ratedVoltage: nodeDef.supportsVoltageSelection ? (nodeDef.defaultVoltage ?? 230) : undefined,
        burned: false,
        timerMs: nodeType === 'TIMER_RELAY' || nodeType === 'MULTIFUNCTION_RELAY' || nodeType === 'BLINKER' ? 1000 : undefined,
        relayMode: nodeType === 'TIMER_RELAY' ? 'ON_DELAY' : nodeType === 'MULTIFUNCTION_RELAY' ? 'AV' : undefined,
        dimmerPercent: nodeType === 'DIMMER' ? 100 : undefined
      }
    };
  }, [getGridPositionForIndex, getLabelPrefix, getNodeTypeCounter]);

  const addNode = React.useCallback(() => {
    if (!canEditM10CadComponents) {
      showSnackbar('Keine Berechtigung zum Hinzufügen von Komponenten.', 'warning');
      return;
    }
    setNodes((previousNodes) => {
      const nextNode = buildNode(selectedType, previousNodes.length, previousNodes);

      window.setTimeout(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        const { width: nodeWidth, height: nodeHeight } = getNodeDimensions(nextNode);

        const visibleLeft = viewport.scrollLeft;
        const visibleTop = viewport.scrollTop;
        const visibleRight = visibleLeft + viewport.clientWidth;
        const visibleBottom = visibleTop + viewport.clientHeight;

        const nodeLeft = nextNode.x;
        const nodeTop = nextNode.y;
        const nodeRight = nodeLeft + nodeWidth;
        const nodeBottom = nodeTop + nodeHeight;

        const horizontalOutside = nodeLeft < visibleLeft || nodeRight > visibleRight;
        const verticalOutside = nodeTop < visibleTop || nodeBottom > visibleBottom;

        if (horizontalOutside || verticalOutside) {
          viewport.scrollTo({
            left: Math.max(0, nodeLeft - GRID_SIZE * 2),
            top: Math.max(0, nodeTop - GRID_SIZE * 2),
            behavior: 'smooth'
          });
        }
      }, 0);

      return [...previousNodes, nextNode];
    });
  }, [buildNode, canEditM10CadComponents, getNodeDimensions, selectedType, showSnackbar]);

  const deleteNode = React.useCallback((nodeId: string) => {
    if (!canEditM10CadComponents) {
      showSnackbar('Keine Berechtigung zum Löschen von Komponenten.', 'warning');
      return;
    }
    setNodes((previousNodes) => previousNodes.filter((node) => node.id !== nodeId));
    setSelectedNodeId((prev) => (prev === nodeId ? null : prev));
    setConnections((previousConnections) => previousConnections.filter((connection) => {
      const sourceNode = nodes.find((node) => node.outputs.some((output) => output.id === connection.fromPinId));
      const targetNode = nodes.find((node) => node.inputs.some((input) => input.id === connection.toPinId));
      return sourceNode?.id !== nodeId && targetNode?.id !== nodeId;
    }));
  }, [canEditM10CadComponents, nodes, showSnackbar]);

  const updateNodeLabel = React.useCallback((nodeId: string, value: string) => {
    setNodes((previousNodes) => previousNodes.map((node) => (node.id === nodeId ? { ...node, label: value } : node)));
  }, []);

  const updateNodeDisplayName = React.useCallback((nodeId: string, value: string) => {
    setNodes((previousNodes) => previousNodes.map((node) => (node.id === nodeId ? { ...node, displayName: value } : node)));
  }, []);

  const updateNodeRatedVoltage = React.useCallback((nodeId: string, value: DeviceVoltage) => {
    setNodes((previousNodes) => previousNodes.map((node) => (
      node.id === nodeId
        ? {
            ...node,
            state: {
              ...node.state,
              ratedVoltage: value
            }
          }
        : node
    )));
  }, []);

  const updateNodeTimerMs = React.useCallback((nodeId: string, value: number) => {
    const normalized = Number.isFinite(value) && value >= 0 ? Math.round(value) : 0;
    setNodes((previousNodes) => previousNodes.map((node) => (
      node.id === nodeId
        ? {
            ...node,
            state: {
              ...node.state,
              timerMs: normalized
            }
          }
        : node
    )));
  }, []);

  const updateNodeRelayMode = React.useCallback((nodeId: string, value: RelayMode) => {
    setNodes((previousNodes) => previousNodes.map((node) => (
      node.id === nodeId
        ? {
            ...node,
            state: {
              ...node.state,
              relayMode: value
            }
          }
        : node
    )));
  }, []);

  const updateNodeDimmerPercent = React.useCallback((nodeId: string, value: number) => {
    const normalized = Math.max(0, Math.min(100, Math.round(value)));
    setNodes((previousNodes) => previousNodes.map((node) => (
      node.id === nodeId
        ? {
            ...node,
            state: {
              ...node.state,
              dimmerPercent: normalized
            }
          }
        : node
    )));
  }, []);

  const toggleNodeState = React.useCallback((nodeId: string) => {
    if (!canUseM10CadSimulation) {
      showSnackbar('Keine Berechtigung für die Simulation.', 'warning');
      return;
    }
    if (!isSimulating) {
      showSnackbar('Schaltbare Geräte sind im Simulationsmodus aktiv.', 'info');
      return;
    }

    let triggerPulseReset = false;

    setNodes((previousNodes) => previousNodes.map((node) => {
      const def = NODE_DEFINITIONS[node.type];
      if (!def.toggleable) return node;
      if (node.id !== nodeId) return node;

      if (node.type === 'TASTER_NO') {
        triggerPulseReset = true;
        return {
          ...node,
          state: {
            ...node.state,
            active: true
          }
        };
      }

      return {
        ...node,
        state: {
          ...node.state,
          active: !node.state.active
        }
      };
    }));

    if (triggerPulseReset) {
      window.setTimeout(() => {
        setNodes((previousNodes) => previousNodes.map((node) => (
          node.id === nodeId
            ? {
                ...node,
                state: {
                  ...node.state,
                  active: false
                }
              }
            : node
        )));
      }, 220);
    }
  }, [canUseM10CadSimulation, isSimulating, showSnackbar]);

  const rearmFuse = React.useCallback((nodeId: string) => {
    if (!canUseM10CadSimulation && !canEditM10CadComponents) {
      showSnackbar('Keine Berechtigung zum Einsichern der Sicherung.', 'warning');
      return;
    }

    let fuseLabel = '';
    setNodes((previousNodes) => previousNodes.map((node) => {
      if (node.id !== nodeId || node.type !== 'FUSE') return node;
      fuseLabel = node.label;
      return {
        ...node,
        state: {
          ...node.state,
          burned: false,
          active: true
        }
      };
    }));

    if (fuseLabel) {
      notifiedBurnedNodesRef.current.delete(nodeId);
      showSnackbar(`Sicherung ${fuseLabel} wurde wieder eingesichert.`, 'success');
    }
  }, [canEditM10CadComponents, canUseM10CadSimulation, showSnackbar]);

  const allPins = React.useMemo(() => nodes.flatMap((node) => [...node.inputs, ...node.outputs]), [nodes]);

  const pinById = React.useMemo(() => {
    const map = new Map<string, Pin>();
    allPins.forEach((pin) => map.set(pin.id, pin));
    return map;
  }, [allPins]);

  const nodeByPinId = React.useMemo(() => {
    const map = new Map<string, CadNode>();
    nodes.forEach((node) => {
      [...node.inputs, ...node.outputs].forEach((pin) => map.set(pin.id, node));
    });
    return map;
  }, [nodes]);

  const findIncomingConnections = React.useCallback((pinId: string): Connection[] => {
    return connections.filter((connection) => connection.toPinId === pinId);
  }, [connections]);

  const resolveInputValue = React.useCallback((inputPinId: string, outputs: Record<string, boolean>): boolean => {
    const incoming = connections.filter((connection) => connection.toPinId === inputPinId);
    if (incoming.length === 0) return false;
    return incoming.some((connection) => Boolean(outputs[connection.fromPinId]));
  }, [connections]);

  const resolveInputPotential = React.useCallback((inputPinId: string, potentials: Record<string, PotentialKey>): PotentialKey => {
    const incoming = connections.filter((connection) => connection.toPinId === inputPinId);
    for (const connection of incoming) {
      const potential = potentials[connection.fromPinId];
      if (potential) return potential;
    }
    return null;
  }, [connections]);

  const findUpstreamSourceType = React.useCallback((targetPinId: string): CadNodeType | null => {
    const sourceTypes: CadNodeType[] = ['L_SOURCE', 'L2_SOURCE', 'L3_SOURCE', 'N_SOURCE', 'PE_SOURCE'];
    const visited = new Set<string>();
    const queue: string[] = [targetPinId];

    while (queue.length > 0) {
      const currentPinId = queue.shift()!;
      if (visited.has(currentPinId)) continue;
      visited.add(currentPinId);

      const incoming = connections.filter((connection) => connection.toPinId === currentPinId);
      for (const connection of incoming) {
        const sourceNode = nodeByPinId.get(connection.fromPinId);
        if (!sourceNode) continue;
        if (sourceTypes.includes(sourceNode.type)) {
          return sourceNode.type;
        }
        sourceNode.inputs.forEach((inputPin) => {
          if (!visited.has(inputPin.id)) queue.push(inputPin.id);
        });
      }
    }

    return null;
  }, [connections, nodeByPinId]);

  const simulateCircuit = React.useCallback((overrides?: Record<string, boolean>, forceEnergized = false): CircuitSimulationResult => {
    const outputValues: Record<string, boolean> = {};
    const potentialOutputs: Record<string, PotentialKey> = {};
    const newlyBurnedNodeIds = new Set<string>();
    const shortCircuitMessages = new Set<string>();
    const simulationActive = isSimulating || forceEnergized;
    const nowMs = Date.now();

    const isPhase = (potential: PotentialKey): potential is 'L1' | 'L2' | 'L3' => potential === 'L1' || potential === 'L2' || potential === 'L3';

    const isTransformerPotential = (potential: PotentialKey): boolean => typeof potential === 'string' && potential.startsWith('T24_');

    const transformerPairInfo = (a: PotentialKey, b: PotentialKey): { sameTrafo: boolean; oppositePole: boolean } => {
      if (!isTransformerPotential(a) || !isTransformerPotential(b)) {
        return { sameTrafo: false, oppositePole: false };
      }
      const [prefixA, sideA, idA] = String(a).split('_');
      const [prefixB, sideB, idB] = String(b).split('_');
      if (prefixA !== 'T24' || prefixB !== 'T24') {
        return { sameTrafo: false, oppositePole: false };
      }
      const sameTrafo = idA === idB;
      const oppositePole = sideA !== sideB;
      return { sameTrafo, oppositePole };
    };

    const calculateVoltage = (a: PotentialKey, b: PotentialKey): number => {
      if (!a || !b) return 0;
      if (a === b) return 0;

      if (isPhase(a) && isPhase(b)) {
        return a === b ? 0 : 400;
      }

      if ((isPhase(a) && (b === 'N' || b === 'PE')) || (isPhase(b) && (a === 'N' || a === 'PE'))) {
        return 230;
      }

      const pair = transformerPairInfo(a, b);
      if (pair.sameTrafo && pair.oppositePole) {
        return 24;
      }

      if ((isTransformerPotential(a) && (isPhase(b) || b === 'N' || b === 'PE')) || (isTransformerPotential(b) && (isPhase(a) || a === 'N' || a === 'PE'))) {
        return 230;
      }

      return 0;
    };

    const classifyPotentialFault = (a: PotentialKey, b: PotentialKey): 'PHASE_SHORT' | 'EARTH_FAULT' | 'SHORT_CIRCUIT' | null => {
      if (!a || !b || a === b) return null;

      if (isPhase(a) && isPhase(b) && a !== b) {
        return 'PHASE_SHORT';
      }

      if ((isPhase(a) && b === 'PE') || (isPhase(b) && a === 'PE')) {
        return 'EARTH_FAULT';
      }

      if ((isPhase(a) && b === 'N') || (isPhase(b) && a === 'N')) {
        return 'SHORT_CIRCUIT';
      }

      return calculateVoltage(a, b) >= 200 ? 'SHORT_CIRCUIT' : null;
    };

    const addPotentialFaultAlarm = (contextLabel: string, a: PotentialKey, b: PotentialKey): boolean => {
      const fault = classifyPotentialFault(a, b);
      if (!fault) return false;

      if (fault === 'PHASE_SHORT') {
        shortCircuitMessages.add(`ALARM Phasenschluss: ${contextLabel} verbindet ${String(a)} mit ${String(b)}.`);
        return true;
      }

      if (fault === 'EARTH_FAULT') {
        shortCircuitMessages.add(`ALARM Erdschluss: ${contextLabel} verbindet Phase mit PE.`);
        return true;
      }

      shortCircuitMessages.add(`ALARM Kurzschluss: ${contextLabel} verbindet ${String(a)} mit ${String(b)}.`);
      return true;
    };

    const markBurnedIfNeeded = (node: CadNode, actualVoltage: number): boolean => {
      const rated = node.state.ratedVoltage ?? 230;
      if (actualVoltage > rated + 0.5) {
        if (!node.state.burned) {
          newlyBurnedNodeIds.add(node.id);
        }
        return true;
      }
      return Boolean(node.state.burned);
    };

    for (let iteration = 0; iteration < 10; iteration += 1) {
      let changed = false;

      nodes.forEach((node) => {
        const inputValues: Record<string, boolean> = {};
        const inputPotentials: Record<string, PotentialKey> = {};
        node.inputs.forEach((inputPin) => {
          inputValues[inputPin.name] = resolveInputValue(inputPin.id, outputValues);
          inputPotentials[inputPin.name] = resolveInputPotential(inputPin.id, potentialOutputs);
        });

        node.inputs.forEach((inputPin) => {
          const incomingPotentials = connections
            .filter((connection) => connection.toPinId === inputPin.id)
            .map((connection) => potentialOutputs[connection.fromPinId])
            .filter((potential): potential is Exclude<PotentialKey, null> => potential !== null);

          for (let leftIndex = 0; leftIndex < incomingPotentials.length; leftIndex += 1) {
            for (let rightIndex = leftIndex + 1; rightIndex < incomingPotentials.length; rightIndex += 1) {
              addPotentialFaultAlarm(`${node.label}.${inputPin.name}`, incomingPotentials[leftIndex], incomingPotentials[rightIndex]);
            }
          }
        });

        const readToggle = (fallback = false): boolean => {
          const overrideValue = overrides?.[node.label];
          if (typeof overrideValue === 'boolean') return overrideValue;
          return Boolean(node.state.active ?? fallback);
        };

        const getDimmerPercentForInputPin = (inputPinId: string): number | null => {
          const incoming = connections.filter((connection) => connection.toPinId === inputPinId);
          for (const connection of incoming) {
            const sourceNode = nodeByPinId.get(connection.fromPinId);
            if (sourceNode?.type === 'DIMMER') {
              if (!Boolean(outputValues[connection.fromPinId])) {
                return 0;
              }
              return sourceNode.state.dimmerPercent ?? 100;
            }
          }
          return null;
        };

        const getInputPinByName = (pinName: string): Pin | undefined => node.inputs.find((pin) => pin.name === pinName);

        const getMinDimmerPercentForPins = (pinNames: string[]): number | null => {
          const levels = pinNames
            .map((pinName) => getInputPinByName(pinName))
            .filter((pin): pin is Pin => Boolean(pin))
            .map((pin) => getDimmerPercentForInputPin(pin.id))
            .filter((level): level is number => typeof level === 'number');
          if (levels.length === 0) return null;
          return Math.min(...levels);
        };

        const nextOutputs: Record<string, boolean> = {};
        const nextPotentials: Record<string, PotentialKey> = {};
        const alreadyBurned = Boolean(node.state.burned);

        switch (node.type) {
          case 'L_SOURCE':
          case 'L2_SOURCE':
          case 'L3_SOURCE':
          case 'N_SOURCE':
          case 'PE_SOURCE': {
            const sourceOn = simulationActive && readToggle(true);
            const outputName = node.outputs[0]?.name || 'OUT';
            nextOutputs[outputName] = sourceOn;
            if (sourceOn) {
              if (node.type === 'L_SOURCE') nextPotentials[outputName] = 'L1';
              if (node.type === 'L2_SOURCE') nextPotentials[outputName] = 'L2';
              if (node.type === 'L3_SOURCE') nextPotentials[outputName] = 'L3';
              if (node.type === 'N_SOURCE') nextPotentials[outputName] = 'N';
              if (node.type === 'PE_SOURCE') nextPotentials[outputName] = 'PE';
            }
            break;
          }
          case 'SWITCH_NO':
          case 'TASTER_NO':
          case 'SENSOR_NO': {
            const closed = readToggle(false) && !alreadyBurned;
            const a = inputPotentials.A;
            const b = inputPotentials.B;
            if (closed) {
              addPotentialFaultAlarm(node.label, a, b);
            }
            const outA = closed ? (b ?? a) : a;
            const outB = closed ? (a ?? b) : b;
            nextOutputs.A = Boolean(outA);
            nextOutputs.B = Boolean(outB);
            nextPotentials.A = outA;
            nextPotentials.B = outB;
            break;
          }
          case 'FUSE': {
            const closed = readToggle(false) && !alreadyBurned;
            const a = inputPotentials.A;
            const b = inputPotentials.B;
            const fuseFault = closed ? addPotentialFaultAlarm(node.label, a, b) : false;
            if (fuseFault && !alreadyBurned) {
              newlyBurnedNodeIds.add(node.id);
            }
            const effectiveClosed = closed && !fuseFault;
            const outA = effectiveClosed ? (b ?? a) : a;
            const outB = effectiveClosed ? (a ?? b) : b;
            nextOutputs.A = Boolean(outA);
            nextOutputs.B = Boolean(outB);
            nextPotentials.A = outA;
            nextPotentials.B = outB;
            break;
          }
          case 'EMERGENCY_STOP_NC': {
            const closed = readToggle(true);
            const a = inputPotentials.A;
            const b = inputPotentials.B;
            if (closed) {
              addPotentialFaultAlarm(node.label, a, b);
            }
            const outA = closed ? (b ?? a) : a;
            const outB = closed ? (a ?? b) : b;
            nextOutputs.A = Boolean(outA);
            nextOutputs.B = Boolean(outB);
            nextPotentials.A = outA;
            nextPotentials.B = outB;
            break;
          }
          case 'OVERLOAD_RELAY': {
            const controlVoltage = calculateVoltage(inputPotentials.A1, inputPotentials.A2);
            const burned = markBurnedIfNeeded(node, controlVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const coilDimmer = getMinDimmerPercentForPins(['A1', 'A2']);
            const enabled = readToggle(true) && !burned && controlVoltage >= rated - 0.5 && (coilDimmer === null || coilDimmer >= 80);
            const poles: Array<[string, string]> = [['L1', 'T1'], ['L2', 'T2'], ['L3', 'T3'], ['N', 'TN']];
            poles.forEach(([inputName, outputName]) => {
              const sourcePotential = inputPotentials[inputName];
              nextOutputs[outputName] = enabled && Boolean(sourcePotential);
              nextPotentials[outputName] = enabled ? sourcePotential : null;
            });
            const comPotential = inputPotentials.COM;
            nextOutputs.AUX_NO = enabled && Boolean(comPotential);
            nextOutputs.AUX_NC = !enabled && Boolean(comPotential);
            nextPotentials.AUX_NO = enabled ? comPotential : null;
            nextPotentials.AUX_NC = enabled ? null : comPotential;
            break;
          }
          case 'CROSS_SWITCH': {
            const crossEnabled = readToggle(false);
            const aOut = crossEnabled ? inputPotentials.B : inputPotentials.A;
            const bOut = crossEnabled ? inputPotentials.A : inputPotentials.B;
            nextOutputs.A_OUT = Boolean(aOut);
            nextOutputs.B_OUT = Boolean(bOut);
            nextPotentials.A_OUT = aOut;
            nextPotentials.B_OUT = bOut;
            break;
          }
          case 'SWITCH_PILOT': {
            const switchOn = Boolean(inputPotentials.L) && readToggle(false);
            nextOutputs.OUT = switchOn;
            nextPotentials.OUT = switchOn ? inputPotentials.L : null;
            nextOutputs.LAMP_ON = switchOn && calculateVoltage(inputPotentials.L, inputPotentials.N) >= 220;
            break;
          }
          case 'RELAY_COIL': {
            const coilVoltage = calculateVoltage(inputPotentials.A1, inputPotentials.A2);
            const burned = markBurnedIfNeeded(node, coilVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const coilDimmer = getMinDimmerPercentForPins(['A1', 'A2']);
            const relayOn = !burned && coilVoltage >= rated - 0.5 && (coilDimmer === null || coilDimmer >= 80);
            const commons = ['COM1', 'COM2'] as const;
            commons.forEach((com, index) => {
              const comPotential = inputPotentials[com];
              const idx = index + 1;
              nextOutputs[`NO${idx}`] = relayOn && Boolean(comPotential);
              nextOutputs[`NC${idx}`] = !relayOn && Boolean(comPotential);
              nextPotentials[`NO${idx}`] = relayOn ? comPotential : null;
              nextPotentials[`NC${idx}`] = relayOn ? null : comPotential;
            });
            break;
          }
          case 'TIMER_RELAY':
          case 'MULTIFUNCTION_RELAY': {
            const coilVoltage = calculateVoltage(inputPotentials.A1, inputPotentials.A2);
            const burned = markBurnedIfNeeded(node, coilVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const coilDimmer = getMinDimmerPercentForPins(['A1', 'A2']);
            const coilOn = !burned && coilVoltage >= rated - 0.5 && (coilDimmer === null || coilDimmer >= 80);
            const delayMs = Math.max(0, node.state.timerMs ?? 1000);
            const mode = node.state.relayMode ?? (node.type === 'MULTIFUNCTION_RELAY' ? 'AV' : 'ON_DELAY');
            const state = relayTimingRef.current[node.id] || { lastCoilOn: false };

            if (coilOn && !state.lastCoilOn) {
              state.onSince = nowMs;
              state.offSince = undefined;
              state.cycleSince = nowMs;
              if (mode === 'PULSE' || mode === 'EW') {
                state.pulseUntil = nowMs + delayMs;
              }
            }
            if (!coilOn && state.lastCoilOn) {
              state.offSince = nowMs;
              state.onSince = undefined;
              if (mode === 'AW') {
                state.pulseUntil = nowMs + delayMs;
              }
            }

            let relayOn = coilOn;
            if (mode === 'ON_DELAY' || mode === 'AV' || mode === 'IA') {
              relayOn = coilOn && (state.onSince !== undefined ? nowMs - state.onSince >= delayMs : false);
            } else if (mode === 'OFF_DELAY' || mode === 'RV') {
              relayOn = coilOn || (state.offSince !== undefined ? nowMs - state.offSince <= delayMs : false);
            } else if (mode === 'PULSE') {
              relayOn = Boolean(state.pulseUntil && nowMs <= state.pulseUntil);
            } else if (mode === 'EW') {
              relayOn = Boolean(state.pulseUntil && nowMs <= state.pulseUntil);
            } else if (mode === 'AW') {
              relayOn = coilOn || Boolean(state.pulseUntil && nowMs <= state.pulseUntil);
            } else if (mode === 'ARV') {
              const onReady = coilOn && (state.onSince !== undefined ? nowMs - state.onSince >= delayMs : false);
              const offHold = !coilOn && (state.offSince !== undefined ? nowMs - state.offSince <= delayMs : false);
              relayOn = onReady || offHold;
            } else if (mode === 'TI' || mode === 'TP') {
              const cycle = Math.max(200, delayMs);
              const age = state.cycleSince !== undefined ? nowMs - state.cycleSince : 0;
              const slot = Math.floor(age / cycle) % 2;
              const pulse = mode === 'TI' ? slot === 0 : slot === 1;
              relayOn = coilOn && pulse;
            } else if (mode === 'ON') {
              relayOn = coilOn;
            } else if (mode === 'OFF') {
              relayOn = false;
            }

            state.lastCoilOn = coilOn;
            relayTimingRef.current[node.id] = state;

            const commons = ['COM1', 'COM2'] as const;
            commons.forEach((com, index) => {
              const comPotential = inputPotentials[com];
              const idx = index + 1;
              nextOutputs[`NO${idx}`] = relayOn && Boolean(comPotential);
              nextOutputs[`NC${idx}`] = !relayOn && Boolean(comPotential);
              nextPotentials[`NO${idx}`] = relayOn ? comPotential : null;
              nextPotentials[`NC${idx}`] = relayOn ? null : comPotential;
            });
            break;
          }
          case 'CONTACTOR_COIL': {
            const coilVoltage = calculateVoltage(inputPotentials.A1, inputPotentials.A2);
            const burned = markBurnedIfNeeded(node, coilVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const coilDimmer = getMinDimmerPercentForPins(['A1', 'A2']);
            nextOutputs.K = !burned && coilVoltage >= rated - 0.5 && (coilDimmer === null || coilDimmer >= 80);
            break;
          }
          case 'RELAY_4CO': {
            const coilVoltage = calculateVoltage(inputPotentials.A1, inputPotentials.A2);
            const burned = markBurnedIfNeeded(node, coilVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const coilDimmer = getMinDimmerPercentForPins(['A1', 'A2']);
            const relayOn = !burned && coilVoltage >= rated - 0.5 && (coilDimmer === null || coilDimmer >= 80);
            const commons = ['COM1', 'COM2', 'COM3', 'COM4'] as const;
            commons.forEach((com, index) => {
              const comPotential = inputPotentials[com];
              const idx = index + 1;
              nextOutputs[`NO${idx}`] = relayOn && Boolean(comPotential);
              nextOutputs[`NC${idx}`] = !relayOn && Boolean(comPotential);
              nextPotentials[`NO${idx}`] = relayOn ? comPotential : null;
              nextPotentials[`NC${idx}`] = relayOn ? null : comPotential;
            });
            break;
          }
          case 'MOTOR_CONTACTOR': {
            const coilVoltage = calculateVoltage(inputPotentials.A1, inputPotentials.A2);
            const burned = markBurnedIfNeeded(node, coilVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const coilDimmer = getMinDimmerPercentForPins(['A1', 'A2']);
            const contactorOn = !burned && coilVoltage >= rated - 0.5 && (coilDimmer === null || coilDimmer >= 80);
            nextOutputs.T1 = contactorOn && Boolean(inputPotentials.L1);
            nextOutputs.T2 = contactorOn && Boolean(inputPotentials.L2);
            nextOutputs.T3 = contactorOn && Boolean(inputPotentials.L3);
            nextPotentials.T1 = contactorOn ? inputPotentials.L1 : null;
            nextPotentials.T2 = contactorOn ? inputPotentials.L2 : null;
            nextPotentials.T3 = contactorOn ? inputPotentials.L3 : null;
            const auxCom = inputPotentials.AUX_COM;
            nextOutputs.AUX_NO = contactorOn && Boolean(auxCom);
            nextOutputs.AUX_NC = !contactorOn && Boolean(auxCom);
            nextPotentials.AUX_NO = contactorOn ? auxCom : null;
            nextPotentials.AUX_NC = contactorOn ? null : auxCom;
            break;
          }
          case 'DIMMER': {
            const runVoltage = calculateVoltage(inputPotentials.L, inputPotentials.N);
            const burned = markBurnedIfNeeded(node, runVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const percent = node.state.dimmerPercent ?? 100;
            const enabled = !burned && readToggle(false) && runVoltage >= rated - 0.5 && percent > 0;
            nextOutputs.OUT = enabled && Boolean(inputPotentials.L);
            nextPotentials.OUT = enabled ? inputPotentials.L : null;
            break;
          }
          case 'BUZZER': {
            const runVoltage = calculateVoltage(inputPotentials.L, inputPotentials.N);
            const burned = markBurnedIfNeeded(node, runVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            nextOutputs.ON = !burned && runVoltage >= rated - 0.5;
            break;
          }
          case 'BLINKER': {
            const runVoltage = calculateVoltage(inputPotentials.L, inputPotentials.N);
            const burned = markBurnedIfNeeded(node, runVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const powered = !burned && runVoltage >= rated - 0.5;
            const period = Math.max(200, node.state.timerMs ?? 1000);
            const pulseOn = powered && Math.floor(nowMs / period) % 2 === 0;
            nextOutputs.OUT = pulseOn && Boolean(inputPotentials.L);
            nextPotentials.OUT = pulseOn ? inputPotentials.L : null;
            break;
          }
          case 'MOTOR_AC': {
            const runVoltage = calculateVoltage(inputPotentials.L, inputPotentials.N);
            const burned = markBurnedIfNeeded(node, runVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const dimmerLevel = getMinDimmerPercentForPins(['L', 'N']);
            nextOutputs.RUN = !burned && runVoltage >= rated - 0.5 && (dimmerLevel === null || dimmerLevel > 0);

            if (isPhase(inputPotentials.PE)) {
              shortCircuitMessages.add(`Kurzschluss erkannt: ${node.label} führt Phase direkt auf PE.`);
            }
            break;
          }
          case 'MOTOR_3PH': {
            const u = inputPotentials.U;
            const v = inputPotentials.V;
            const w = inputPotentials.W;
            const hasValidPhases = isPhase(u) && isPhase(v) && isPhase(w) && new Set([u, v, w]).size === 3;
            nextOutputs.RUN = hasValidPhases;

            if (isPhase(inputPotentials.PE)) {
              shortCircuitMessages.add(`Kurzschluss erkannt: ${node.label} führt Phase direkt auf PE.`);
            }
            break;
          }
          case 'CHANGEOVER': {
            const switchedToNo = readToggle(false) || Boolean(inputValues.SEL);
            nextOutputs.NO = Boolean(inputPotentials.L) && switchedToNo;
            nextOutputs.NC = Boolean(inputPotentials.L) && !switchedToNo;
            nextPotentials.NO = switchedToNo ? inputPotentials.L : null;
            nextPotentials.NC = switchedToNo ? null : inputPotentials.L;
            break;
          }
          case 'TRANSFORMER': {
            const transformerOn = calculateVoltage(inputPotentials.PRI_L, inputPotentials.PRI_N) >= 220;
            nextOutputs.SEC_L = transformerOn;
            nextOutputs.SEC_N = transformerOn;
            nextPotentials.SEC_L = transformerOn ? `T24_L_${node.id}` : null;
            nextPotentials.SEC_N = transformerOn ? `T24_N_${node.id}` : null;
            break;
          }
          case 'SOCKET': {
            const voltage = calculateVoltage(inputPotentials.L, inputPotentials.N);
            nextOutputs.READY = voltage >= 220 && Boolean(inputPotentials.PE);
            break;
          }
          case 'LAMP': {
            const lampVoltage = calculateVoltage(inputPotentials.L, inputPotentials.N);
            const burned = markBurnedIfNeeded(node, lampVoltage);
            const rated = node.state.ratedVoltage ?? 230;
            const dimmerLevel = getMinDimmerPercentForPins(['L', 'N']);
            nextOutputs.ON = !burned && lampVoltage >= rated - 0.5 && (dimmerLevel === null || dimmerLevel > 0);
            break;
          }
          case 'JUNCTION':
            nextOutputs.OUT = Boolean(inputPotentials.IN);
            nextPotentials.OUT = inputPotentials.IN;
            break;
          case 'JUNCTION_BOX':
            break;
          default:
            break;
        }

        node.outputs.forEach((outputPin) => {
          const nextPotential = nextPotentials[outputPin.name] ?? null;
          const nextBoolean = nextPotential ? true : Boolean(nextOutputs[outputPin.name]);
          if (outputValues[outputPin.id] !== nextBoolean || potentialOutputs[outputPin.id] !== nextPotential) changed = true;
          outputValues[outputPin.id] = nextBoolean;
          potentialOutputs[outputPin.id] = nextPotential;
        });
      });

      if (!changed) break;
    }

    const lampStates: Record<string, boolean> = {};
    nodes.filter((node) => node.type === 'LAMP').forEach((lampNode) => {
      const outPin = lampNode.outputs.find((pin) => pin.name === 'ON');
      lampStates[lampNode.label] = outPin ? Boolean(outputValues[outPin.id]) : false;
    });

    const motorStates: Record<string, MotorVisualState> = {};
    nodes.forEach((node) => {
      if (node.type === 'MOTOR_AC') {
        const runPin = node.outputs.find((pin) => pin.name === 'RUN');
        motorStates[node.id] = {
          running: runPin ? Boolean(outputValues[runPin.id]) : false,
          direction: 'CW'
        };
      }

      if (node.type === 'MOTOR_3PH') {
        const runPin = node.outputs.find((pin) => pin.name === 'RUN');
        const running = runPin ? Boolean(outputValues[runPin.id]) : false;

        const uPin = node.inputs.find((pin) => pin.name === 'U');
        const vPin = node.inputs.find((pin) => pin.name === 'V');
        const wPin = node.inputs.find((pin) => pin.name === 'W');

        const sourceU = uPin ? resolveInputPotential(uPin.id, potentialOutputs) : null;
        const sourceV = vPin ? resolveInputPotential(vPin.id, potentialOutputs) : null;
        const sourceW = wPin ? resolveInputPotential(wPin.id, potentialOutputs) : null;

        const toPhaseIndex = (source: PotentialKey): number | undefined => {
          if (source === 'L1') return 1;
          if (source === 'L2') return 2;
          if (source === 'L3') return 3;
          return undefined;
        };

        const sequence = [sourceU, sourceV, sourceW]
          .map((source) => toPhaseIndex(source))
          .filter((value): value is number => typeof value === 'number');

        let direction: MotorDirection = 'CW';
        if (sequence.length === 3 && new Set(sequence).size === 3) {
          const inversions = (sequence[0] > sequence[1] ? 1 : 0)
            + (sequence[0] > sequence[2] ? 1 : 0)
            + (sequence[1] > sequence[2] ? 1 : 0);
          direction = inversions % 2 === 0 ? 'CW' : 'CCW';
        }

        motorStates[node.id] = {
          running,
          direction
        };
      }

      if ((node.type === 'LAMP' || node.type === 'MOTOR_AC' || node.type === 'RELAY_COIL' || node.type === 'RELAY_4CO' || node.type === 'TIMER_RELAY' || node.type === 'MULTIFUNCTION_RELAY' || node.type === 'CONTACTOR_COIL' || node.type === 'MOTOR_CONTACTOR' || node.type === 'OVERLOAD_RELAY' || node.type === 'DIMMER' || node.type === 'BUZZER' || node.type === 'BLINKER') && newlyBurnedNodeIds.has(node.id)) {
        shortCircuitMessages.add(`${node.label} ist durch Überspannung durchgebrannt.`);
      }
    });

    connections.forEach((connection) => {
      const sourcePotential = potentialOutputs[connection.fromPinId];
      const targetPin = pinById.get(connection.toPinId);
      const targetNode = targetPin ? nodeByPinId.get(targetPin.id) : null;
      if (!targetPin || !targetNode) return;

      if (isPhase(sourcePotential) && targetPin.name === 'PE') {
        shortCircuitMessages.add(`Kurzschluss erkannt: Phase auf PE an ${targetNode.label}.`);
      }

      const nonConsumer = targetNode.type !== 'LAMP' && targetNode.type !== 'MOTOR_AC' && targetNode.type !== 'MOTOR_3PH' && targetNode.type !== 'TRANSFORMER' && targetNode.type !== 'SOCKET';
      if (isPhase(sourcePotential) && (targetPin.name === 'N' || targetPin.name === 'PE') && nonConsumer) {
        shortCircuitMessages.add(`Kurzschluss erkannt: direkte Phase auf ${targetPin.name} ohne Verbraucher (${targetNode.label}).`);
      }
    });

    return {
      outputValues,
      lampStates,
      motorStates,
      newlyBurnedNodeIds: Array.from(newlyBurnedNodeIds),
      shortCircuitMessages: Array.from(shortCircuitMessages)
    };
  }, [connections, isSimulating, nodeByPinId, nodes, pinById, resolveInputPotential, resolveInputValue]);

  const recalculatePinPositions = React.useCallback(() => {
    const hostRect = canvasRef.current?.getBoundingClientRect();
    if (!hostRect) return;

    const nextPositions: Record<string, { x: number; y: number }> = {};

    Object.entries(pinRefs.current).forEach(([pinId, element]) => {
      if (!element) return;
      const rect = element.getBoundingClientRect();
      nextPositions[pinId] = {
        x: rect.left - hostRect.left + rect.width / 2,
        y: rect.top - hostRect.top + rect.height / 2
      };
    });

    setPinPositions(nextPositions);
  }, []);

  const schedulePinPositionRecalc = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    if (pinRecalcFrameRef.current !== null) {
      window.cancelAnimationFrame(pinRecalcFrameRef.current);
    }
    pinRecalcFrameRef.current = window.requestAnimationFrame(() => {
      pinRecalcFrameRef.current = null;
      recalculatePinPositions();
    });
  }, [recalculatePinPositions]);

  const setPinRef = React.useCallback((pinId: string, element: unknown) => {
    pinRefs.current[pinId] = element instanceof HTMLElement ? element : null;
    schedulePinPositionRecalc();
  }, [schedulePinPositionRecalc]);

  React.useLayoutEffect(() => {
    schedulePinPositionRecalc();
  }, [nodes, connections, blockScale, schedulePinPositionRecalc]);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    const handleLayoutChange = () => schedulePinPositionRecalc();

    schedulePinPositionRecalc();
    const timeout = window.setTimeout(handleLayoutChange, 20);
    window.addEventListener('resize', handleLayoutChange);
    window.addEventListener('orientationchange', handleLayoutChange);
    viewport?.addEventListener('scroll', handleLayoutChange, { passive: true });

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('resize', handleLayoutChange);
      window.removeEventListener('orientationchange', handleLayoutChange);
      viewport?.removeEventListener('scroll', handleLayoutChange);
      if (pinRecalcFrameRef.current !== null) {
        window.cancelAnimationFrame(pinRecalcFrameRef.current);
        pinRecalcFrameRef.current = null;
      }
    };
  }, [schedulePinPositionRecalc]);

  React.useEffect(() => {
    if (!isSimulating) {
      setLivePinValues({});
      setMotorStates({});
      lastShortCircuitRef.current = '';
      relayTimingRef.current = {};
      return;
    }

    const runLive = () => {
      const result = simulateCircuit();
      setLivePinValues(result.outputValues);
      setMotorStates(result.motorStates);

      if (result.newlyBurnedNodeIds.length > 0) {
        setNodes((previousNodes) => previousNodes.map((node) => (
          result.newlyBurnedNodeIds.includes(node.id)
            ? {
                ...node,
                state: {
                  ...node.state,
                  burned: true
                }
              }
            : node
        )));

        const justNotified = result.newlyBurnedNodeIds.filter((nodeId) => !notifiedBurnedNodesRef.current.has(nodeId));
        if (justNotified.length > 0) {
          justNotified.forEach((nodeId) => notifiedBurnedNodesRef.current.add(nodeId));
          const affectedNodes = nodes
            .filter((node) => justNotified.includes(node.id))
          const trippedFuses = affectedNodes.filter((node) => node.type === 'FUSE').map((node) => node.label);
          const burnedOtherLabels = affectedNodes.filter((node) => node.type !== 'FUSE').map((node) => node.label);

          if (trippedFuses.length > 0 && burnedOtherLabels.length > 0) {
            showSnackbar(`SFG ausgelöst: ${trippedFuses.join(', ')}. Bitte einsichern. Weitere Schäden: ${burnedOtherLabels.join(', ')}.`, 'error');
          } else if (trippedFuses.length > 0) {
            showSnackbar(`SFG ausgelöst: ${trippedFuses.join(', ')}. Bitte einsichern.`, 'error');
          } else if (burnedOtherLabels.length > 0) {
            showSnackbar(`Durchgebrannt: ${burnedOtherLabels.join(', ')}. Bitte Bauteil ersetzen.`, 'error');
          }
        }
      }

      const shortSignature = result.shortCircuitMessages.join('|');
      if (result.shortCircuitMessages.length > 0 && shortSignature !== lastShortCircuitRef.current) {
        lastShortCircuitRef.current = shortSignature;
        showSnackbar(`🚨 ${result.shortCircuitMessages[0]}`, 'error');
      }
      if (result.shortCircuitMessages.length === 0) {
        lastShortCircuitRef.current = '';
      }
    };

    runLive();
    const interval = window.setInterval(runLive, 180);
    return () => window.clearInterval(interval);
  }, [connections, isSimulating, nodes, showSnackbar, simulateCircuit]);

  const handlePinClick = React.useCallback((pin: Pin) => {
    if (!canEditM10CadConnections) {
      showSnackbar('Keine Berechtigung für Verbindungsbearbeitung.', 'warning');
      return;
    }
    if (isSimulating) {
      showSnackbar('Im Simulationsmodus sind Verbindungsänderungen gesperrt.', 'warning');
      return;
    }

    const currentlySelected = selectedPinId ? pinById.get(selectedPinId) : null;

    if (!currentlySelected) {
      setSelectedPinId(pin.id);
      setDraftRoutePoints([]);
      return;
    }

    if (currentlySelected.id === pin.id) {
      setSelectedPinId(null);
      setDraftRoutePoints([]);
      return;
    }

    if (currentlySelected.isInput === pin.isInput) {
      setSelectedPinId(pin.id);
      setDraftRoutePoints([]);
      return;
    }

    const fromPinId = currentlySelected.isInput ? pin.id : currentlySelected.id;
    const toPinId = currentlySelected.isInput ? currentlySelected.id : pin.id;

    if (fromPinId && toPinId) {
      setConnections((previousConnections) => {
        return [...previousConnections, {
          id: createId(),
          fromPinId,
          toPinId,
          routePoints: routeMode === 'FREE' ? draftRoutePoints : []
        }];
      });
      setSelectedPinId(null);
      setDraftRoutePoints([]);
      return;
    }
  }, [canEditM10CadConnections, draftRoutePoints, isSimulating, pinById, routeMode, selectedPinId, showSnackbar]);

  const getCanvasPointer = React.useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const canvasElement = canvasRef.current;
    const viewport = viewportRef.current;
    if (!canvasElement || !viewport) return null;

    const canvasRect = canvasElement.getBoundingClientRect();
    return {
      x: clientX - canvasRect.left + viewport.scrollLeft,
      y: clientY - canvasRect.top + viewport.scrollTop
    };
  }, []);

  const handleCanvasRoutingClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (routeMode !== 'FREE' || !selectedPinId || isSimulating) return;
    const selectedPin = pinById.get(selectedPinId);
    if (!selectedPin || selectedPin.isInput) return;

    const point = getCanvasPointer(event.clientX, event.clientY);
    if (!point) return;

    setDraftRoutePoints((previous) => [...previous, {
      x: snapToGrid(point.x),
      y: snapToGrid(point.y)
    }]);
  }, [getCanvasPointer, isSimulating, pinById, routeMode, selectedPinId, snapToGrid]);

  const removeConnection = React.useCallback((connectionId: string) => {
    if (!canEditM10CadConnections) {
      showSnackbar('Keine Berechtigung zum Löschen von Verbindungen.', 'warning');
      return;
    }
    if (isSimulating) {
      showSnackbar('Im Simulationsmodus sind Verbindungen gesperrt.', 'warning');
      return;
    }
    setConnections((previousConnections) => previousConnections.filter((connection) => connection.id !== connectionId));
  }, [canEditM10CadConnections, isSimulating, showSnackbar]);

  const resetEditor = React.useCallback(() => {
    if (!canEditM10CadComponents) {
      showSnackbar('Keine Berechtigung zum Zurücksetzen.', 'warning');
      return;
    }
    if (isSimulating) {
      showSnackbar('Simulation zuerst stoppen.', 'warning');
      return;
    }
    setNodes([]);
    setConnections([]);
    setSelectedPinId(null);
    setDraftRoutePoints([]);
    setSelectedNodeId(null);
    setLivePinValues({});
    setMotorStates({});
    notifiedBurnedNodesRef.current.clear();
    lastShortCircuitRef.current = '';
    relayTimingRef.current = {};
  }, [canEditM10CadComponents, isSimulating, showSnackbar]);

  const autoLayout = React.useCallback(() => {
    setNodes((previousNodes) => previousNodes.map((node, index) => ({
      ...node,
      ...getGridPositionForIndex(index)
    })));
  }, [getGridPositionForIndex]);

  const canvasSize = React.useMemo(() => {
    const maxX = nodes.reduce((max, node) => Math.max(max, node.x), 0);
    const maxY = nodes.reduce((max, node) => Math.max(max, node.y), 0);
    return {
      width: Math.max(1600, maxX + 500),
      height: Math.max(1000, maxY + 360)
    };
  }, [nodes]);

  const crossReferenceNodes = React.useMemo(() => {
    return [...nodes]
      .filter((node) => node.type === 'SWITCH_NO' || node.type === 'CROSS_SWITCH' || node.type === 'SWITCH_PILOT' || node.type === 'TASTER_NO' || node.type === 'SENSOR_NO' || node.type === 'RELAY_COIL' || node.type === 'RELAY_4CO' || node.type === 'TIMER_RELAY' || node.type === 'MULTIFUNCTION_RELAY' || node.type === 'CONTACTOR_COIL' || node.type === 'MOTOR_CONTACTOR' || node.type === 'MOTOR_AC' || node.type === 'MOTOR_3PH' || node.type === 'EMERGENCY_STOP_NC' || node.type === 'OVERLOAD_RELAY')
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [nodes]);

  const selectedNode = React.useMemo(() => nodes.find((node) => node.id === selectedNodeId) || null, [nodes, selectedNodeId]);

  const jumpToNode = React.useCallback((nodeId: string) => {
    const node = nodes.find((item) => item.id === nodeId);
    const viewport = viewportRef.current;
    if (!node || !viewport) return;
    viewport.scrollTo({
      left: Math.max(0, node.x - viewport.clientWidth / 3),
      top: Math.max(0, node.y - viewport.clientHeight / 3),
      behavior: 'smooth'
    });
    setSelectedNodeId(nodeId);
  }, [nodes]);

  const beginNodeDrag = React.useCallback((event: React.MouseEvent<HTMLDivElement>, nodeId: string) => {
    if (!canEditM10CadComponents || isSimulating) return;
    const node = nodes.find((item) => item.id === nodeId);
    const pointer = getCanvasPointer(event.clientX, event.clientY);
    if (!node || !pointer) return;

    setDragState({
      nodeId,
      offsetX: pointer.x - node.x,
      offsetY: pointer.y - node.y
    });
    setSelectedNodeId(nodeId);
    event.preventDefault();
  }, [canEditM10CadComponents, getCanvasPointer, isSimulating, nodes]);

  const beginPaletteDrag = React.useCallback((event: React.DragEvent<HTMLElement>, nodeType: CadNodeType) => {
    if (!canEditM10CadComponents || isSimulating) return;
    event.dataTransfer.setData('application/x-cad-node-type', nodeType);
    event.dataTransfer.effectAllowed = 'copy';
  }, [canEditM10CadComponents, isSimulating]);

  const handleCanvasDragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!canEditM10CadComponents || isSimulating) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, [canEditM10CadComponents, isSimulating]);

  const handleCanvasDrop = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!canEditM10CadComponents || isSimulating) return;
    event.preventDefault();
    const rawType = event.dataTransfer.getData('application/x-cad-node-type');
    if (!rawType || !(rawType in NODE_DEFINITIONS)) return;

    const pointer = getCanvasPointer(event.clientX, event.clientY);
    if (!pointer) return;

    setNodes((previousNodes) => {
      const nodeType = rawType as CadNodeType;
      const nextNode = buildNode(nodeType, previousNodes.length, previousNodes);
      const { width: nodeWidth, height: nodeHeight } = getNodeDimensions(nextNode);
      return [
        ...previousNodes,
        {
          ...nextNode,
          x: Math.max(GRID_SIZE, snapToGrid(pointer.x - nodeWidth / 2)),
          y: Math.max(GRID_SIZE, snapToGrid(pointer.y - nodeHeight / 2))
        }
      ];
    });
  }, [buildNode, canEditM10CadComponents, getCanvasPointer, getNodeDimensions, isSimulating, snapToGrid]);

  React.useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (event: MouseEvent) => {
      const canvasElement = canvasRef.current;
      const viewport = viewportRef.current;
      if (!canvasElement || !viewport) return;

      const canvasRect = canvasElement.getBoundingClientRect();
      const pointerX = event.clientX - canvasRect.left + viewport.scrollLeft;
      const pointerY = event.clientY - canvasRect.top + viewport.scrollTop;

      const nextX = Math.max(GRID_SIZE, snapToGrid(pointerX - dragState.offsetX));
      const nextY = Math.max(GRID_SIZE, snapToGrid(pointerY - dragState.offsetY));

      setNodes((previousNodes) => {
        const movingNode = previousNodes.find((node) => node.id === dragState.nodeId);
        if (!movingNode) return previousNodes;

        const deltaX = nextX - movingNode.x;
        const deltaY = nextY - movingNode.y;
        if (deltaX === 0 && deltaY === 0) return previousNodes;

        setConnections((previousConnections) => {
          if (previousConnections.length === 0) return previousConnections;

          const movingInputPinIds = new Set(movingNode.inputs.map((inputPin) => inputPin.id));
          const movingOutputPinIds = new Set(movingNode.outputs.map((outputPin) => outputPin.id));
          let changed = false;

          const nextConnections = previousConnections.map((connection) => {
            const routePoints = connection.routePoints;
            if (!routePoints || routePoints.length === 0) return connection;

            const sourceMoved = movingOutputPinIds.has(connection.fromPinId);
            const targetMoved = movingInputPinIds.has(connection.toPinId);
            if (!sourceMoved && !targetMoved) return connection;

            if (sourceMoved && targetMoved) {
              changed = true;
              return {
                ...connection,
                routePoints: routePoints.map((point) => ({
                  x: snapToGrid(point.x + deltaX),
                  y: snapToGrid(point.y + deltaY)
                }))
              };
            }

            const adjustedRoutePoints = routePoints.map((point) => ({ ...point }));

            if (sourceMoved && adjustedRoutePoints.length > 0) {
              const firstPoint = adjustedRoutePoints[0];
              adjustedRoutePoints[0] = {
                x: snapToGrid(firstPoint.x + deltaX),
                y: snapToGrid(firstPoint.y + deltaY)
              };
            }

            if (targetMoved && adjustedRoutePoints.length > 0) {
              const lastIndex = adjustedRoutePoints.length - 1;
              const lastPoint = adjustedRoutePoints[lastIndex];
              adjustedRoutePoints[lastIndex] = {
                x: snapToGrid(lastPoint.x + deltaX),
                y: snapToGrid(lastPoint.y + deltaY)
              };
            }

            changed = true;
            return {
              ...connection,
              routePoints: adjustedRoutePoints
            };
          });

          return changed ? nextConnections : previousConnections;
        });

        return previousNodes.map((node) => (
          node.id === dragState.nodeId
            ? {
                ...node,
                x: nextX,
                y: nextY
              }
            : node
        ));
      });

      schedulePinPositionRecalc();
    };

    const handleMouseUp = () => {
      setDragState(null);
      schedulePinPositionRecalc();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, schedulePinPositionRecalc, snapToGrid]);

  const saveProject = React.useCallback(() => {
    if (!canSaveM10CadProjects) {
      showSnackbar('Keine Berechtigung für Speichern/Laden.', 'warning');
      return;
    }
    const payload = {
      version: 1,
      pageId: PAGE_ID,
      selectedTaskIndex,
      nodes,
      connections
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cad_editor_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    showSnackbar('Projekt exportiert.', 'success');
  }, [canSaveM10CadProjects, connections, nodes, selectedTaskIndex, showSnackbar]);

  const loadProjectFromFile = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canSaveM10CadProjects) {
      showSnackbar('Keine Berechtigung für Speichern/Laden.', 'warning');
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!Array.isArray(parsed?.nodes) || !Array.isArray(parsed?.connections)) {
          throw new Error('Ungültiges Projektformat.');
        }
        setNodes(parsed.nodes);
        setConnections(parsed.connections);
        if (typeof parsed?.selectedTaskIndex === 'number' && parsed.selectedTaskIndex >= 0 && parsed.selectedTaskIndex < TASKS.length) {
          setSelectedTaskIndex(parsed.selectedTaskIndex);
        }
        showSnackbar('Projekt geladen.', 'success');
      } catch (error: any) {
        showSnackbar(error?.message || 'Projekt konnte nicht geladen werden.', 'error');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  }, [canSaveM10CadProjects, showSnackbar]);

  const collectStructuralErrors = React.useCallback((): string[] => {
    const errors: string[] = [];

    if (!nodes.some((node) => node.type === 'L_SOURCE')) {
      errors.push('Es fehlt mindestens eine Phasenquelle (L_SOURCE).');
    }
    if (!nodes.some((node) => node.type === 'N_SOURCE')) {
      errors.push('Es fehlt mindestens eine Masse/Neutralleiter-Quelle (N_SOURCE).');
    }

    nodes.forEach((node) => {
      if (node.type === 'RELAY_COIL' || node.type === 'CONTACTOR_COIL' || node.type === 'RELAY_4CO' || node.type === 'TIMER_RELAY' || node.type === 'MULTIFUNCTION_RELAY' || node.type === 'MOTOR_CONTACTOR' || node.type === 'OVERLOAD_RELAY') {
        const a1Pin = node.inputs.find((pin) => pin.name === 'A1');
        const a2Pin = node.inputs.find((pin) => pin.name === 'A2');

        if (a1Pin && findIncomingConnections(a1Pin.id).length === 0) {
          errors.push(`Bei ${node.label} fehlt A1 (Ansteuerung).`);
        }
        if (a2Pin && findIncomingConnections(a2Pin.id).length === 0) {
          errors.push(`Bei ${node.label} fehlt A2/Masse am Relais/Schütz.`);
        }
      }

      if (node.type === 'LAMP') {
        const lPin = node.inputs.find((pin) => pin.name === 'L');
        const nPin = node.inputs.find((pin) => pin.name === 'N');
        if (lPin && findIncomingConnections(lPin.id).length === 0) {
          errors.push(`Bei Lampe ${node.label} fehlt die Phase (L).`);
        }
        if (nPin && findIncomingConnections(nPin.id).length === 0) {
          errors.push(`Bei Lampe ${node.label} fehlt Neutralleiter/Masse (N).`);
        }
      }

      if (node.type === 'SOCKET') {
        const lPin = node.inputs.find((pin) => pin.name === 'L');
        const nPin = node.inputs.find((pin) => pin.name === 'N');
        const pePin = node.inputs.find((pin) => pin.name === 'PE');
        if (lPin && findIncomingConnections(lPin.id).length === 0) {
          errors.push(`Bei Steckdose ${node.label} fehlt Phase (L).`);
        }
        if (nPin && findIncomingConnections(nPin.id).length === 0) {
          errors.push(`Bei Steckdose ${node.label} fehlt Neutralleiter (N).`);
        }
        if (pePin && findIncomingConnections(pePin.id).length === 0) {
          errors.push(`Bei Steckdose ${node.label} fehlt Schutzleiter (PE).`);
        }
      }

      if (node.state.burned) {
        errors.push(`Bauteil ${node.label} ist durchgebrannt und muss ersetzt werden.`);
      }
    });

    return Array.from(new Set(errors));
  }, [findIncomingConnections, nodes]);

  const evaluateTask = React.useCallback(() => {
    if (!canValidateM10CadTasks) {
      showSnackbar('Keine Berechtigung zur Aufgabenprüfung.', 'warning');
      return;
    }

    const task = TASKS[selectedTaskIndex];
    const errors = collectStructuralErrors();

    task.requiredTypes.forEach((requiredType) => {
      if (!nodes.some((node) => node.type === requiredType)) {
        errors.push(`Aufgabe verlangt Komponente: ${NODE_DEFINITIONS[requiredType].label}.`);
      }
    });

    (task.requiredLabels || []).forEach((label) => {
      if (!nodes.some((node) => node.label === label)) {
        errors.push(`Benötigtes Gerät mit Label "${label}" fehlt.`);
      }
    });

    const vectorResults: ValidationResult[] = task.vectors.map((vector, vectorIndex) => {
      const simulated = simulateCircuit(vector.inputs, true);
      const actual: Record<string, boolean> = {};
      Object.keys(vector.expected).forEach((outputLabel) => {
        actual[outputLabel] = Boolean(simulated.lampStates[outputLabel]);
      });
      const pass = Object.keys(vector.expected).every((outputLabel) => actual[outputLabel] === vector.expected[outputLabel]);
      return {
        vectorIndex,
        inputs: vector.inputs,
        expected: vector.expected,
        actual,
        pass
      };
    });

    const allPassed = vectorResults.every((result) => result.pass);

    if (!allPassed) {
      errors.push('Mindestens ein Testvektor wurde nicht korrekt erfüllt.');
    }

    setValidationErrors(Array.from(new Set(errors)));
    setValidationResults(vectorResults);
    setValidationOpen(true);

    if (allPassed && errors.length === 0) {
      showSnackbar('Aufgabe bestanden. Schaltung ist korrekt.', 'success');
    } else {
      showSnackbar('Prüfung abgeschlossen: Es wurden Fehler gefunden.', 'error');
    }
  }, [canValidateM10CadTasks, collectStructuralErrors, nodes, selectedTaskIndex, showSnackbar, simulateCircuit]);

  const saveRequiredLevel = React.useCallback(async () => {
    if (!token || !canEditM10CadAccess) return;
    try {
      setSubmittingAccess(true);
      setAccessError(null);
      setAccessSuccess(null);
      const numericValue = parseInt(requiredLevel, 10);
      if (!Number.isFinite(numericValue) || numericValue < 0) {
        setAccessError('Bitte eine gueltige Zahl >= 0 eingeben.');
        return;
      }
      const design = { ...(override?.design || {}), requiredLevel: numericValue };
      await updateUnitContent(PAGE_ID, { design }, token);
      setAccessSuccess('Mindestpunktzahl gespeichert.');
      await refresh();
    } catch (error: any) {
      setAccessError(error?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSubmittingAccess(false);
    }
  }, [canEditM10CadAccess, override?.design, refresh, requiredLevel, token]);

  if (!hasCadPrivilege && totalScore < currentRequired) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          Für dieses Modul benötigst du mindestens {currentRequired} Punkte. Deine aktuellen Punkte: {totalScore}
        </Alert>
        <Navigate to="/" replace />
      </Box>
    );
  }

  const currentTask = TASKS[selectedTaskIndex];

  return (
    <UnitPageShell title={currentTask.title} maxWidth={false}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%' }}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 20, backgroundColor: tokens.colors.primary }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: tokens.colors.dark.text }}>
              CAD Schaltschrank-Editor
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => setInfoOpen(true)} sx={{ color: tokens.colors.dark.text }} title="Info">
                <InfoOutlinedIcon />
              </IconButton>
              <IconButton onClick={evaluateTask} sx={{ color: tokens.colors.dark.text }} title="Aufgabe prüfen" disabled={!canValidateM10CadTasks}>
                <CheckCircleIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ px: { xs: 1, sm: 2 }, py: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Mindestpunktzahl: {currentRequired} · Dein Punktestand: {totalScore}
          </Typography>
          {canEditM10CadAccess ? (
            <Button variant="contained" onClick={() => setAccessDialogOpen(true)}>
              Mindestpunktzahl festlegen
            </Button>
          ) : (
            <Alert severity="info" sx={{ m: 0, px: 1.5, py: 0.5 }}>
              Nur berechtigte Nutzer können die Mindestpunktzahl ändern.
            </Alert>
          )}
        </Box>

        <Paper sx={{ ...getCardSx(darkMode), p: 1.5, mb: 1, mx: { xs: 1, sm: 2 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="cad-component-label">Komponente</InputLabel>
              <Select
                labelId="cad-component-label"
                value={selectedType}
                label="Komponente"
                onChange={(event) => setSelectedType(event.target.value as CadNodeType)}
                disabled={!canEditM10CadComponents || isSimulating}
              >
                {(Object.keys(NODE_DEFINITIONS) as CadNodeType[]).map((nodeType) => (
                  <MenuItem key={nodeType} value={nodeType}>
                    {NODE_DEFINITIONS[nodeType].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button startIcon={<AddIcon />} onClick={addNode} disabled={!canEditM10CadComponents || isSimulating}>
              Gerät hinzufügen
            </Button>
            <Button onClick={autoLayout} disabled={nodes.length === 0}>Auto-Layout</Button>
            <Button startIcon={isSimulating ? <PauseIcon /> : <PlayArrowIcon />} onClick={() => setIsSimulating((previous) => !previous)} disabled={!canUseM10CadSimulation}>
              {isSimulating ? 'Simulation stoppen' : 'Simulation starten'}
            </Button>
            <Button startIcon={<RestartAltIcon />} color="error" onClick={resetEditor}>
              Reset
            </Button>
            <Button startIcon={<SaveIcon />} onClick={saveProject} disabled={!canSaveM10CadProjects}>
              Export
            </Button>
            <Button startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()} disabled={!canSaveM10CadProjects}>
              Import
            </Button>
            <Button
              variant={routeMode === 'DIRECT' ? 'contained' : 'outlined'}
              onClick={() => {
                setRouteMode('DIRECT');
                setDraftRoutePoints([]);
              }}
            >
              Leitung: Punkt-zu-Punkt
            </Button>
            <Button
              variant={routeMode === 'FREE' ? 'contained' : 'outlined'}
              onClick={() => setRouteMode('FREE')}
            >
              Leitung: Frei zeichnen
            </Button>
            <Button
              onClick={() => setDraftRoutePoints([])}
              disabled={draftRoutePoints.length === 0}
            >
              Freilinie löschen
            </Button>
            <TextField
              size="small"
              label="Kennzeichen"
              value={selectedNode?.label || ''}
              onChange={(event) => {
                if (selectedNode) {
                  updateNodeLabel(selectedNode.id, event.target.value);
                }
              }}
              disabled={!selectedNode || !canEditM10CadComponents || isSimulating}
              sx={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              label="Name"
              value={selectedNode?.displayName || ''}
              onChange={(event) => {
                if (selectedNode) {
                  updateNodeDisplayName(selectedNode.id, event.target.value);
                }
              }}
              disabled={!selectedNode || !canEditM10CadComponents || isSimulating}
              sx={{ minWidth: 200 }}
            />
            {selectedNode && NODE_DEFINITIONS[selectedNode.type].supportsVoltageSelection && (
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel id="cad-voltage-label">Nennspannung</InputLabel>
                <Select
                  labelId="cad-voltage-label"
                  value={String(selectedNode.state.ratedVoltage ?? NODE_DEFINITIONS[selectedNode.type].defaultVoltage ?? 230)}
                  label="Nennspannung"
                  onChange={(event) => updateNodeRatedVoltage(selectedNode.id, Number(event.target.value) as DeviceVoltage)}
                  disabled={!canEditM10CadComponents || isSimulating || Boolean(selectedNode.state.burned)}
                >
                  <MenuItem value="24">24 V</MenuItem>
                  <MenuItem value="230">230 V</MenuItem>
                </Select>
              </FormControl>
            )}
            {selectedNode && (selectedNode.type === 'TIMER_RELAY' || selectedNode.type === 'MULTIFUNCTION_RELAY') && (
              <TextField
                size="small"
                label="Zeit (ms)"
                type="number"
                value={selectedNode.state.timerMs ?? 1000}
                onChange={(event) => updateNodeTimerMs(selectedNode.id, Number(event.target.value))}
                disabled={!canEditM10CadComponents || isSimulating || Boolean(selectedNode.state.burned)}
                sx={{ minWidth: 120 }}
                inputProps={{ min: 0, step: 100 }}
              />
            )}
            {selectedNode && (selectedNode.type === 'TIMER_RELAY' || selectedNode.type === 'MULTIFUNCTION_RELAY') && (
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="cad-relay-mode-label">Relaismodus</InputLabel>
                <Select
                  labelId="cad-relay-mode-label"
                  value={selectedNode.state.relayMode ?? 'ON_DELAY'}
                  label="Relaismodus"
                  onChange={(event) => updateNodeRelayMode(selectedNode.id, event.target.value as RelayMode)}
                  disabled={!canEditM10CadComponents || isSimulating || Boolean(selectedNode.state.burned)}
                >
                  <MenuItem value="ON_DELAY">Anzugsverzögerung</MenuItem>
                  <MenuItem value="OFF_DELAY">Abfallverzögerung</MenuItem>
                  <MenuItem value="PULSE">Impuls</MenuItem>
                </Select>
              </FormControl>
            )}
            <Box component="input" ref={fileInputRef} type="file" accept="application/json" sx={{ display: 'none' }} onChange={loadProjectFromFile} />
          </Stack>

          <Divider sx={{ my: 1.25 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.2, display: 'block' }}>
            Bauteile per Drag-and-Drop aus der Legende links auf das Raster ziehen. Für freie Leitungen: Ausgang wählen, Zwischenpunkte klicken, Ziel-Eingang wählen.
          </Typography>
        </Paper>

        <Paper sx={{ ...getCardSx(darkMode), p: 2, mb: 1, mx: { xs: 1, sm: 2 } }}>
          <Tabs
            value={selectedTaskIndex}
            onChange={(_event, value) => setSelectedTaskIndex(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 1.5 }}
          >
            {TASKS.map((task, index) => (
              <Tab key={task.title} label={`Aufgabe ${index + 1}`} />
            ))}
          </Tabs>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
            {currentTask.title}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{currentTask.description}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {currentTask.requiredTypes.map((requiredType) => (
              <Chip key={requiredType} size="small" label={NODE_DEFINITIONS[requiredType].label} variant="outlined" />
            ))}
          </Stack>
        </Paper>

        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2, flexGrow: 1 }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} sx={{ alignItems: 'stretch' }}>
            <Accordion
              defaultExpanded
              sx={{
                width: { xs: '100%', lg: 180 },
                flexShrink: 0,
                border: '1px solid #222222',
                borderRadius: 0,
                backgroundColor: '#FFFFFF',
                alignSelf: { xs: 'stretch', lg: 'flex-start' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Legende</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0.75 }}>
                <Box
                  ref={legendScrollRef}
                  onScroll={handleLegendScroll}
                  sx={{
                    maxHeight: { xs: 280, lg: 'calc(100vh - 330px)' },
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    overscrollBehavior: 'contain',
                    pr: 0.25
                  }}
                >
                  <Stack direction="column" spacing={0.7}>
                    {legendLoopTypes.map((deviceType, index) => (
                      <Box
                        key={`${deviceType}-${index}`}
                        draggable={canEditM10CadComponents && !isSimulating}
                        onDragStart={(event) => beginPaletteDrag(event, deviceType)}
                        sx={{
                          border: '1px solid #222222',
                          borderRadius: 0,
                          px: 0.5,
                          py: 0.45,
                          backgroundColor: '#FFFFFF',
                          cursor: canEditM10CadComponents && !isSimulating ? 'grab' : 'default',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.2
                        }}
                      >
                        <Box sx={{ transform: 'scale(0.74)', transformOrigin: 'center center', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <DeviceSymbol type={deviceType} darkMode={darkMode} />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1, textAlign: 'center' }}>
                          {NODE_DEFINITIONS[deviceType].label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Paper sx={{ p: 1.25, border: '1px solid #222222', borderRadius: 0, backgroundColor: '#FFFFFF' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Querverweise</Typography>
                {crossReferenceNodes.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">Noch keine Schalter/Taster/Sensoren/Spulen vorhanden.</Typography>
                ) : (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {crossReferenceNodes.map((node) => (
                      <Chip
                        key={node.id}
                        size="small"
                        variant={selectedNodeId === node.id ? 'filled' : 'outlined'}
                        color={selectedNodeId === node.id ? 'primary' : 'default'}
                        label={node.label}
                        onClick={() => jumpToNode(node.id)}
                      />
                    ))}
                  </Stack>
                )}
              </Paper>

              <Paper
                ref={viewportRef}
                sx={{
                  minHeight: 640,
                  overflowX: 'auto',
                  overflowY: 'auto',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #222222',
                  flexGrow: 1
                }}
              >
            <Box
              ref={canvasRef}
              onDragOver={handleCanvasDragOver}
              onDrop={handleCanvasDrop}
              onClick={handleCanvasRoutingClick}
              sx={{ position: 'relative', width: canvasSize.width, height: canvasSize.height, minHeight: 640 }}
            >
              <Box sx={{ position: 'absolute', left: 16, top: 8 }}>
                <Typography variant="caption" sx={{ color: '#1F2937' }}>Schaltplan in aufgelöster Darstellung • Raster mit Drag-and-Drop • Horizontal unbegrenzt scrollbar</Typography>
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `linear-gradient(to right, rgba(17,17,17,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(17,17,17,0.08) 1px, transparent 1px)`,
                  backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
                  pointerEvents: 'none'
                }}
              />

              <Box component="svg" sx={{ position: 'absolute', inset: 0, width: canvasSize.width, height: canvasSize.height, pointerEvents: 'none', zIndex: 30 }}>
                <line x1="0" y1="56" x2={canvasSize.width} y2="56" stroke="#222222" strokeWidth="1" />
                <line x1="0" y1={canvasSize.height - 36} x2={canvasSize.width} y2={canvasSize.height - 36} stroke="#222222" strokeWidth="1" />
              </Box>

              <Box component="svg" sx={{ position: 'absolute', inset: 0, width: canvasSize.width, height: canvasSize.height, pointerEvents: 'none', zIndex: 60 }}>
                {connections.map((connection) => {
                  const from = pinPositions[connection.fromPinId];
                  const to = pinPositions[connection.toPinId];
                  if (!from || !to) return null;
                  const fromValue = Boolean(livePinValues[connection.fromPinId]);
                  const strokeColor = fromValue ? '#0F766E' : '#222222';
                  const allPoints = [
                    `${from.x},${from.y}`,
                    ...((connection.routePoints || []).map((point) => `${point.x},${point.y}`)),
                    `${to.x},${to.y}`
                  ];
                  return (
                    <g key={connection.id} style={{ pointerEvents: 'auto', cursor: 'pointer' }} onClick={(event) => {
                      event.stopPropagation();
                      removeConnection(connection.id);
                    }}>
                      <polyline
                        points={allPoints.join(' ')}
                        fill="none"
                        stroke={strokeColor}
                        strokeOpacity={0.65}
                        strokeWidth={2}
                        strokeLinejoin="miter"
                        strokeLinecap="square"
                      />
                    </g>
                  );
                })}

                {selectedPinId && draftRoutePoints.length > 0 && (() => {
                  const selectedPin = pinById.get(selectedPinId);
                  if (!selectedPin || selectedPin.isInput) return null;
                  const from = pinPositions[selectedPin.id];
                  if (!from) return null;
                  const points = [`${from.x},${from.y}`, ...draftRoutePoints.map((point) => `${point.x},${point.y}`)];
                  return (
                    <polyline
                      points={points.join(' ')}
                      fill="none"
                      stroke="#1D4ED8"
                      strokeWidth={2}
                      strokeDasharray="5 4"
                    />
                  );
                })()}
              </Box>

              {nodes.map((node) => {
                const nodeDefinition = NODE_DEFINITIONS[node.type];
                const isSelectedNode = selectedNodeId === node.id || Boolean(selectedPinId && [...node.inputs, ...node.outputs].some((pin) => pin.id === selectedPinId));
                const isJunctionNode = node.type === 'JUNCTION';
                const isJunctionBoxNode = node.type === 'JUNCTION_BOX';
                const nodeDimensions = getNodeDimensions(node);
                const nodePinSlotWidth = Math.max(basePinSlotWidth, nodeDimensions.pinSlotWidth || basePinSlotWidth);
                const nodeInputPin = isJunctionNode ? node.inputs[0] : undefined;
                const nodeOutputPin = isJunctionNode ? node.outputs[0] : undefined;
                const lampOutputPin = node.outputs.find((outputPin) => outputPin.name === 'ON');
                const isLampOn = lampOutputPin ? Boolean(livePinValues[lampOutputPin.id]) : false;
                const motorState = motorStates[node.id];
                const isMotorNode = node.type === 'MOTOR_AC' || node.type === 'MOTOR_3PH';
                const isMotorRunning = Boolean(motorState?.running);
                const motorDirectionLabel = motorState?.direction === 'CCW' ? 'Linkslauf' : 'Rechtslauf';
                const toggleButtonLabel = node.type === 'CHANGEOVER'
                  ? (node.state.active ? 'NO aktiv' : 'NC aktiv')
                  : (node.state.active ? 'EIN' : 'AUS');

                return (
                  <Box
                    key={node.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedNodeId(node.id);
                    }}
                    onMouseDown={(event) => {
                      if (isJunctionNode || isJunctionBoxNode) {
                        beginNodeDrag(event, node.id);
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      left: node.x,
                      top: node.y,
                      width: nodeDimensions.width,
                      minHeight: nodeDimensions.height,
                      p: isJunctionNode ? 0.1 : 0.45,
                      borderRadius: 0,
                      border: isSelectedNode ? '2px solid #1D4ED8' : '1px solid #222222',
                      bgcolor: '#FFFFFF',
                      zIndex: 12
                    }}
                  >
                    {!isJunctionNode && !isJunctionBoxNode && (
                    <Box
                      onMouseDown={(event) => beginNodeDrag(event, node.id)}
                      sx={{
                        mb: 0.8,
                        px: 0.8,
                        py: 0.3,
                        border: '1px dashed #6B7280',
                        cursor: canEditM10CadComponents && !isSimulating ? 'grab' : 'not-allowed',
                        userSelect: 'none',
                        backgroundColor: '#F9FAFB'
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>Raster-Drag: greifen und ziehen</Typography>
                    </Box>
                    )}

                    {!isJunctionNode && !isJunctionBoxNode && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.15, px: 0.25 }}>
                      {node.inputs.length > 0 ? node.inputs.map((inputPin) => {
                        const selected = selectedPinId === inputPin.id;
                        const fedValue = resolveInputValue(inputPin.id, livePinValues);
                        return (
                          <Stack key={inputPin.id} spacing={0.25} alignItems="center" sx={{ minWidth: nodePinSlotWidth, maxWidth: nodePinSlotWidth }}>
                            <Box
                              ref={(element) => { setPinRef(inputPin.id, element); }}
                              onClick={(event) => {
                                event.stopPropagation();
                                handlePinClick(inputPin);
                              }}
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                border: '1px solid #222222',
                                backgroundColor: selected ? tokens.colors.primary : fedValue ? '#0F766E' : '#FFFFFF'
                              }}
                            />
                            <Typography variant="caption" sx={{ lineHeight: 1.02, fontSize: 9.5, width: '100%', textAlign: 'center', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', minHeight: 14 }}>{inputPin.name}</Typography>
                          </Stack>
                        );
                      }) : <Box sx={{ height: 16 }} />}
                    </Stack>
                    )}

                    {isJunctionNode && nodeInputPin && nodeOutputPin && (
                      <Box sx={{ position: 'relative', width: scaledJunctionSize, height: scaledJunctionSize }}>
                        <Box
                          ref={(element) => {
                            setPinRef(nodeInputPin.id, element);
                            setPinRef(nodeOutputPin.id, element);
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            const selectedPin = selectedPinId ? pinById.get(selectedPinId) : null;
                            if (selectedPin && !selectedPin.isInput) {
                              handlePinClick(nodeInputPin);
                              return;
                            }
                            handlePinClick(nodeOutputPin);
                          }}
                          sx={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            border: '1px solid #222222',
                            backgroundColor: Boolean(selectedPinId && (selectedPinId === nodeInputPin.id || selectedPinId === nodeOutputPin.id)) ? tokens.colors.primary : '#FFFFFF'
                          }}
                        />
                      </Box>
                    )}

                    {!isJunctionNode && !isJunctionBoxNode && (
                    <>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          transform: `scale(${blockSymbolScale})`,
                          transformOrigin: 'left center',
                          width: 98 * blockSymbolScale,
                          height: 58 * blockSymbolScale,
                          flexShrink: 0
                        }}
                      >
                        <DeviceSymbol type={node.type} darkMode={darkMode} active={Boolean(node.state.active) || isLampOn} />
                      </Box>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        {node.type === 'JUNCTION' ? (
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>Klemmpunkt</Typography>
                        ) : (
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{node.label}</Typography>
                        )}
                        {node.displayName && node.type !== 'JUNCTION' && <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{node.displayName}</Typography>}
                        {node.type !== 'JUNCTION' && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{nodeDefinition.label}</Typography>}
                        {nodeDefinition.supportsVoltageSelection && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                            Nennspannung: {node.state.ratedVoltage ?? nodeDefinition.defaultVoltage ?? 230} V
                          </Typography>
                        )}
                        {(node.type === 'TIMER_RELAY' || node.type === 'MULTIFUNCTION_RELAY') && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                            Zeit: {node.state.timerMs ?? 1000} ms · Modus: {node.state.relayMode ?? 'ON_DELAY'}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title="Komponente löschen" arrow>
                        <span>
                          <IconButton size="small" onClick={(event) => {
                            event.stopPropagation();
                            deleteNode(node.id);
                          }} disabled={!canEditM10CadComponents || isSimulating}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>

                    {nodeDefinition.supportsVoltageSelection && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.35, minHeight: 22, fontSize: 10 }}
                        disabled={Boolean(node.state.burned) || isSimulating}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateNodeRatedVoltage(node.id, (node.state.ratedVoltage ?? nodeDefinition.defaultVoltage ?? 230) === 230 ? 24 : 230);
                        }}
                      >
                        {`Spannung: ${node.state.ratedVoltage ?? nodeDefinition.defaultVoltage ?? 230}V`}
                      </Button>
                    )}

                    {(node.type === 'TIMER_RELAY' || node.type === 'MULTIFUNCTION_RELAY' || node.type === 'BLINKER') && (
                      <Stack direction="row" spacing={0.4} sx={{ mt: 0.35 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ minHeight: 22, fontSize: 10 }}
                          disabled={Boolean(node.state.burned) || isSimulating}
                          onClick={(event) => {
                            event.stopPropagation();
                            const current = node.state.relayMode ?? 'ON_DELAY';
                            const next: RelayMode = current === 'ON_DELAY' ? 'OFF_DELAY' : current === 'OFF_DELAY' ? 'PULSE' : 'ON_DELAY';
                            updateNodeRelayMode(node.id, next);
                          }}
                        >
                          {`Modus: ${node.state.relayMode === 'OFF_DELAY' ? 'TOF' : node.state.relayMode === 'PULSE' ? 'PULSE' : 'TON'}`}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ minHeight: 22, fontSize: 10 }}
                          disabled={Boolean(node.state.burned) || isSimulating}
                          onClick={(event) => {
                            event.stopPropagation();
                            const current = node.state.timerMs ?? 1000;
                            const next = current >= 5000 ? 250 : current + 250;
                            updateNodeTimerMs(node.id, next);
                          }}
                        >
                          {`Zeit: ${node.state.timerMs ?? 1000}ms`}
                        </Button>
                      </Stack>
                    )}

                    {nodeDefinition.toggleable && (
                      <Button
                        size="small"
                        variant={node.state.active ? 'contained' : 'outlined'}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleNodeState(node.id);
                        }}
                        startIcon={node.state.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        sx={{ mt: 0.35, minHeight: 24, fontSize: 11 }}
                        disabled={Boolean(node.state.burned)}
                      >
                        {toggleButtonLabel}
                      </Button>
                    )}

                    {node.type === 'FUSE' && node.state.burned && (
                      <>
                        <Chip size="small" color="error" label="SFG AUSGELÖST" sx={{ mt: 0.45, ml: 0.4, fontWeight: 700 }} />
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          sx={{ mt: 0.35, minHeight: 22, fontSize: 10 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            rearmFuse(node.id);
                          }}
                          disabled={!canUseM10CadSimulation && !canEditM10CadComponents}
                        >
                          Einsichern
                        </Button>
                      </>
                    )}

                    {node.state.burned && node.type !== 'FUSE' && (
                      <Chip size="small" color="error" label="DURCHGEBRANNT" sx={{ mt: 0.5, ml: 0.5 }} />
                    )}

                    {node.type === 'LAMP' && (
                      <Chip size="small" color={isLampOn ? 'success' : 'default'} label={isLampOn ? 'LEUCHTET' : 'AUS'} sx={{ mt: 0.5, ml: 0.5 }} />
                    )}

                    {isMotorNode && (
                      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.5, ml: 0.2 }}>
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            border: '2px solid #111111',
                            position: 'relative',
                            '@keyframes cadMotorSpinCw': {
                              from: { transform: 'rotate(0deg)' },
                              to: { transform: 'rotate(360deg)' }
                            },
                            '@keyframes cadMotorSpinCcw': {
                              from: { transform: 'rotate(0deg)' },
                              to: { transform: 'rotate(-360deg)' }
                            },
                            animation: isMotorRunning
                              ? `${motorState?.direction === 'CCW' ? 'cadMotorSpinCcw' : 'cadMotorSpinCw'} 0.8s linear infinite`
                              : 'none'
                          }}
                        >
                          <Box sx={{ position: 'absolute', left: 9, top: 2, width: 2, height: 8, backgroundColor: '#111111' }} />
                        </Box>
                        <Chip
                          size="small"
                          color={isMotorRunning ? 'success' : 'default'}
                          label={isMotorRunning ? `LÄUFT · ${motorDirectionLabel}` : 'MOTOR AUS'}
                        />
                      </Stack>
                    )}
                    </>
                    )}

                    {!isJunctionNode && <Box sx={{ height: 24 }} />}

                    {!isJunctionNode && !isJunctionBoxNode && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.4, px: 0.25 }}>
                      {node.outputs.length > 0 ? node.outputs.map((outputPin) => {
                        const selected = selectedPinId === outputPin.id;
                        const pinValue = Boolean(livePinValues[outputPin.id]);
                        return (
                          <Stack key={outputPin.id} spacing={0.25} alignItems="center" sx={{ minWidth: nodePinSlotWidth, maxWidth: nodePinSlotWidth }}>
                            <Typography variant="caption" sx={{ lineHeight: 1.02, fontSize: 9.5, width: '100%', textAlign: 'center', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', minHeight: 14 }}>{outputPin.name}</Typography>
                            <Box
                              ref={(element) => { setPinRef(outputPin.id, element); }}
                              onClick={(event) => {
                                event.stopPropagation();
                                handlePinClick(outputPin);
                              }}
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                border: '1px solid #222222',
                                backgroundColor: selected ? tokens.colors.primary : pinValue ? '#0F766E' : '#FFFFFF'
                              }}
                            />
                          </Stack>
                        );
                      }) : <Box sx={{ height: 16 }} />}
                    </Stack>
                    )}
                  </Box>
                );
              })}

              {nodes.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Noch keine Komponenten vorhanden.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Wähle oben eine Komponente und klicke auf „Gerät hinzufügen".
                  </Typography>
                </Box>
              )}
            </Box>
              </Paper>
            </Box>
          </Stack>
        </Box>
      </Box>

      <Dialog open={validationOpen} onClose={() => setValidationOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Prüfergebnis – {currentTask.title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {validationErrors.length > 0 && (
              <Alert severity="error">
                <Stack spacing={0.5}>
                  {validationErrors.map((errorText) => (
                    <Typography key={errorText} variant="body2">• {errorText}</Typography>
                  ))}
                </Stack>
              </Alert>
            )}

            {validationResults.map((result) => (
              <Paper key={result.vectorIndex} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle2">Testfall {result.vectorIndex + 1}</Typography>
                  <Chip size="small" color={result.pass ? 'success' : 'error'} label={result.pass ? 'PASS' : 'FEHLER'} />
                </Stack>
                <Typography variant="body2">Eingänge: {JSON.stringify(result.inputs)}</Typography>
                <Typography variant="body2">Erwartet: {JSON.stringify(result.expected)}</Typography>
                <Typography variant="body2">Aktuell: {JSON.stringify(result.actual)}</Typography>
              </Paper>
            ))}

            {validationResults.length > 0 && validationResults.every((result) => result.pass) && validationErrors.length === 0 && (
              <Alert severity="success">Alle Testfälle bestanden. Schaltung ist korrekt.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Info-Center CAD-Editor</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body2">- Der CAD-Bereich ist als ein zusammenhängender Schaltplan aufgebaut (keine Netzwerk-Blöcke).</Typography>
            <Typography variant="body2">- Das Layout läuft über ein Raster (Grid) und ist horizontal wie vertikal aufgebaut.</Typography>
            <Typography variant="body2">- Geräte können per Drag-and-Drop am Ziehgriff im Raster positioniert werden.</Typography>
            <Typography variant="body2">- Bei Bauteilen weiter rechts wird automatisch horizontal gescrollt (Breite ist dynamisch).</Typography>
            <Typography variant="body2">- Verbindungen sind standardmäßig Punkt-zu-Punkt. Optional kannst du Leitungen frei über Zwischenpunkte zeichnen.</Typography>
            <Typography variant="body2">- Es stehen L1, L2, L3, N und PE als Quellen zur Verfügung (inkl. Drehstromaufbau).</Typography>
            <Typography variant="body2">- Die Legende ist links klappbar und dient als Drag-Quelle: Bauteile direkt aus der Legende auf das Raster ziehen.</Typography>
            <Typography variant="body2">- Wechselstrom- und Drehstrommotoren zeigen Laufzustand und Drehrichtung als Animation an.</Typography>
            <Typography variant="body2">- Über „Querverweise" springst du schnell zwischen Schaltern/Tastern/Sensoren/Spulen.</Typography>
            <Typography variant="body2">- Jedes Bauteil hat ein eigenes SVG-Symbol; die Legende zeigt eine kompakte Symbol-Liste mit kurzer Bezeichnung.</Typography>
            <Typography variant="body2">- Jedes Gerät besitzt Kennzeichen und einen frei editierbaren Namen.</Typography>
            <Typography variant="body2">- Komponenten hinzufügen und per Klick auf Pin-Ausgang → Pin-Eingang verbinden.</Typography>
            <Typography variant="body2">- Leitungen sind klickbar und werden so gelöscht.</Typography>
            <Typography variant="body2">- Simulation starten, dann Schalter/Taster/Sensoren umschalten.</Typography>
            <Typography variant="body2">- Grün bedeutet TRUE/aktiv, grau bedeutet FALSE/inaktiv.</Typography>
            <Typography variant="body2">- Mit dem Haken wird die Schaltung gegen die aktuelle Aufgabe geprüft.</Typography>
            <Typography variant="body2">- Bei Fehlern öffnet sich ein Popup mit konkreter Fehlerbeschreibung (z. B. fehlende Masse am Relais).</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={accessDialogOpen} onClose={() => setAccessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Einstellungen: Zugangsvoraussetzung</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Die Mindestpunktzahl steuert den Zugriff auf diese Seite. Ohne ausreichende Punkte bleibt die Seite gesperrt.
          </Typography>
          <TextField
            label="Mindestpunktzahl"
            type="number"
            fullWidth
            value={requiredLevel}
            onChange={(event) => setRequiredLevel(event.target.value)}
            inputProps={{ min: 0 }}
          />
          {accessError && <Alert sx={{ mt: 2 }} severity="error">{accessError}</Alert>}
          {accessSuccess && <Alert sx={{ mt: 2 }} severity="success">{accessSuccess}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessDialogOpen(false)} disabled={submittingAccess}>Abbrechen</Button>
          <Button onClick={saveRequiredLevel} variant="contained" disabled={submittingAccess}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(initialSnackbar)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(initialSnackbar)}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </UnitPageShell>
  );
};

export default M10_U2_X2_CAD;
